import React, { useState, useEffect } from 'react';
import './ApplyForms.css';
import hLogoImg from '../HLOGO.png';
import { API_URL } from '../config/api';
import {
  extractIdNumberFromFile,
  formatGovernmentIdNumber,
  getUploadOcrMapping,
} from '../utils/idNumberExtraction';
import {
  buildApplicantDisplayName,
  getGovIdValidationErrors,
  govIdNameMatchesApplicant,
  validateGovernmentIdsForApplicant,
} from '../utils/govIdNameValidation';

const BPO_EXPERIENCE_CHOICES = {
  YES: 'Yes, I have BPO experience',
  FRESH_GRAD: "No, I'm a Fresh Graduate",
  NON_BPO: 'No, but I have non-BPO work experience',
};

const BPO_DURATION_OPTIONS = [
  { value: 'shifter-1', tier: 'Shifter 1', label: '6 months - 1 year', labelZh: '6个月 - 1年' },
  { value: 'shifter-2', tier: 'Shifter 2', label: '1 year - 3 years', labelZh: '1年 - 3年' },
  { value: 'shifter-3', tier: 'Shifter 3', label: '3 years - 4 years', labelZh: '3年 - 4年' },
  { value: 'shifter-4', tier: 'Shifter 4', label: '5 years and up', labelZh: '5年及以上' },
];

const NON_BPO_EXPERIENCE_OPTIONS = [
  { value: 'adapter', label: 'Adapter', labelZh: 'Adapter', desc: 'Work experience is not related to BPO', descZh: '工作经验与 BPO 无关' },
  { value: 'starter', label: 'Starter', labelZh: 'Starter', desc: 'No experience at all', descZh: '完全没有工作经验' },
];

const getExperienceLevelLabel = (value, lang = 'en') => {
  const bpoMatch = BPO_DURATION_OPTIONS.find((opt) => opt.value === value);
  if (bpoMatch) {
    const duration = lang === 'zh' ? bpoMatch.labelZh : bpoMatch.label;
    return `${bpoMatch.tier} (${duration})`;
  }
  const nonBpoMatch = NON_BPO_EXPERIENCE_OPTIONS.find((opt) => opt.value === value);
  if (nonBpoMatch) {
    const desc = lang === 'zh' ? nonBpoMatch.descZh : nonBpoMatch.desc;
    return `${nonBpoMatch.label} — ${desc}`;
  }
  return value || '';
};

const APPLICATION_DRAFT_KEY = 'helport_application_draft';

const createEmptyFormData = () => ({
  firstName: '',
  lastName: '',
  middleName: '',
  suffix: '',
  currentResidentialAddress: '',
  schoolName: '',
  yearGraduated: '',
  latestCompanyName: '',
  totalExperience: '',
  reasonForLeaving: '',
  email: '',
  mobileNumber: '',
  isMinor: '',
  dateOfBirth: '',
  highestEducation: '',
  bpoExperience: '',
  experienceLevel: '',
  bpoDurationLevel: '',
  nonBpoCategory: '',
  gender: '',
  civilStatus: '',
  nationality: '',
  religion: '',
  dateOfApplication: '',
  sourceOfReferral: '',
  referredBy: '',
  hasRelative: '',
  relativeName: '',
  referralRelation: '',
  permanentAddress: '',
  permanentSameAsCurrent: false,
  emergencyContactName: '',
  emergencyContactRelation: '',
  emergencyContactMobile: '',
  tinNumber: '',
  tinNameOnId: '',
  sssNumber: '',
  sssNameOnId: '',
  philHealthNumber: '',
  philHealthNameOnId: '',
  pagIbigNumber: '',
  pagIbigNameOnId: '',
  position: '',
  workSetup: '',
  shiftingSchedule: '',
  workSchedule: '',
  nbiStatus: '',
  currentHealthStatus: '',
  hasRelativeHPBO: '',
  relativeHPBOName: '',
  relativeHPBORelation: '',
  englishOralSkills: '',
  englishWrittenSkills: '',
  otherLanguages: '',
  otherLanguagesWrittenSkills: '',
  otherLanguagesOralSkills: '',
  totalAllExperience: '',
  totalRelevantExperience: '',
  pastEmploymentStatus: '',
  isCurrentlyEmployed: '',
  currentEmployer: '',
  currentRole: '',
  currentEmploymentPeriod: '',
  noticePeriod: '',
  hasOutstandingJobOffer: '',
  comprehensiveBackgroundCheck: '',
  drugFreeWorkplace: '',
  pastInfractionsStatus: '',
  employmentHistory1st: '',
  reasonForSeparation1st: '',
  clearanceAndCoe1st: '',
  employmentHistory2nd: '',
  reasonForSeparation2nd: '',
  clearanceAndCoe2nd: '',
  employmentHistory3rd: '',
  reasonForSeparation3rd: '',
  clearanceAndCoe3rd: '',
  employmentHistory4th: '',
  reasonForSeparation4th: '',
  clearanceAndCoe4th: '',
  employmentHistory5th: '',
  reasonForSeparation5th: '',
  clearanceAndCoe5th: '',
  employmentHistory6th: '',
  reasonForSeparation6th: '',
  clearanceAndCoe6th: '',
  employmentHistory7th: '',
  reasonForSeparation7th: '',
  clearanceAndCoe7th: '',
  employmentHistory8th: '',
  reasonForSeparation8th: '',
  clearanceAndCoe8th: '',
  employmentHistory9th: '',
  reasonForSeparation9th: '',
  clearanceAndCoe9th: '',
  employmentHistory10th: '',
  reasonForSeparation10th: '',
  clearanceAndCoe10th: '',
  applicantDeclarationAgreement: '',
  specialAccommodations: '',
  dataPrivacyConsent: '',
  cityOrMunicipality: '',
});

const readApplicationDraft = (applyMode = 'new') => {
  try {
    const parsed = JSON.parse(localStorage.getItem(APPLICATION_DRAFT_KEY) || 'null');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    if ((parsed.applyMode || 'new') !== applyMode) return null;
    return parsed;
  } catch {
    return null;
  }
};

const saveApplicationDraft = (payload) => {
  try {
    localStorage.setItem(APPLICATION_DRAFT_KEY, JSON.stringify({
      ...payload,
      savedAt: Date.now(),
    }));
  } catch (error) {
    console.warn('Could not save application draft:', error);
  }
};

const clearApplicationDraft = () => {
  try {
    localStorage.removeItem(APPLICATION_DRAFT_KEY);
  } catch {
    // ignore storage errors
  }
};

const ApplyForm = ({ onBack, lang = 'en', initialStep = 1, applyMode = 'new', onSubmitSuccess }) => {
  const [step, setStep] = useState(() => {
    const draft = readApplicationDraft(applyMode);
    if (draft?.step && Number(draft.step) >= 1) {
      return Number(draft.step);
    }
    return initialStep || 1;
  });

  const [formData, setFormData] = useState(() => {
    const draft = readApplicationDraft(applyMode);
    if (draft?.formData && typeof draft.formData === 'object') {
      const merged = { ...createEmptyFormData(), ...draft.formData };
      const past = String(merged.pastEmploymentStatus || '');
      if (past && (/January 1,?\s*2025/i.test(past) || /Jan\s*\.\s*1/i.test(past) || /present date/i.test(past) || /within the period/i.test(past))) {
        if (/^Yes,/i.test(past)) {
          merged.pastEmploymentStatus = 'Yes, I was employed up to current date.';
        } else if (/^No,/i.test(past)) {
          merged.pastEmploymentStatus = 'No, I was not employed up to current date.';
        }
      }
      return merged;
    }
    return createEmptyFormData();
  });
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [ocrStatus, setOcrStatus] = useState({});
  const [ocrProgress, setOcrProgress] = useState({});
  const [fileUploads, setFileUploads] = useState({
    resumeFiles: [],
    birthCertificateFiles: [],
    tinFiles: [],
    sssFiles: [],
    philHealthFiles: [],
    pagIbigFiles: [],
    coeFiles: [],
    nbiFiles: []
  });

  useEffect(() => {
    if (initialStep && initialStep > 1) {
      setStep((prev) => (prev > initialStep ? prev : initialStep));
    }
  }, [initialStep]);

  useEffect(() => {
    saveApplicationDraft({
      applyMode,
      step,
      formData,
    });
  }, [applyMode, step, formData]);

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, dataUrl: reader.result });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const cacheApplicantUploads = async (email) => {
    const normalizedEmail = (email || '').trim().toLowerCase();
    if (!normalizedEmail) return;

    const sections = [
      ['resume_file', fileUploads.resumeFiles],
      ['birth_cert_file', fileUploads.birthCertificateFiles],
      ['tin_file', fileUploads.tinFiles],
      ['sss_file', fileUploads.sssFiles],
      ['philhealth_file', fileUploads.philHealthFiles],
      ['pagibig_file', fileUploads.pagIbigFiles],
      ['coe_file', fileUploads.coeFiles],
      ['nbi_file', fileUploads.nbiFiles]
    ];

    const cache = {};
    for (const [key, files] of sections) {
      if (!files?.length) continue;
      cache[key] = await Promise.all(files.map((file) => readFileAsDataUrl(file)));
    }

    try {
      localStorage.setItem(`helport_upload_cache_${normalizedEmail}`, JSON.stringify(cache));
    } catch (error) {
      console.warn('Could not cache uploaded files locally:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${sizes[i]}`;
  };

  const handleFileChange = async (field, files) => {
    const selectedFiles = Array.from(files || []);
    setFileUploads((prev) => ({
      ...prev,
      [field]: selectedFiles,
    }));

    const mapping = getUploadOcrMapping(field);
    if (!mapping || selectedFiles.length === 0) {
      return;
    }

    const { docType, formKey, nameFormKey } = mapping;
    setOcrStatus((prev) => ({ ...prev, [docType]: 'reading' }));
    setOcrProgress((prev) => ({ ...prev, [docType]: 0 }));

    try {
      const result = await extractIdNumberFromFile(
        selectedFiles[0],
        docType,
        (progress) => setOcrProgress((prev) => ({ ...prev, [docType]: progress }))
      );

      if (!result.supported) {
        setOcrStatus((prev) => ({ ...prev, [docType]: 'unsupported' }));
        return;
      }

      if (result.number || result.name) {
        setFormData((prev) => ({
          ...prev,
          ...(result.number ? { [formKey]: formatGovernmentIdNumber(docType, result.number) } : {}),
          ...(result.name ? { [nameFormKey]: result.name } : {}),
        }));
        setOcrStatus((prev) => ({ ...prev, [docType]: 'success' }));
        return;
      }

      setOcrStatus((prev) => ({ ...prev, [docType]: 'failed' }));
    } catch (error) {
      console.error('ID document OCR failed:', error);
      setOcrStatus((prev) => ({ ...prev, [docType]: 'failed' }));
    }
  };

  const renderOcrStatus = (docType) => {
    const status = ocrStatus[docType];
    if (!status) return null;

    const progress = ocrProgress[docType] || 0;
    const messages = {
      reading: lang === 'zh'
        ? `正在读取文件中的号码... ${progress}%`
        : `Reading the number from your upload... ${progress}%`,
      success: lang === 'zh'
        ? '号码和姓名已从上传文件中读取并自动填入。'
        : 'Number and name were read from your upload and filled in automatically.',
      failed: lang === 'zh'
        ? '无法从该文件自动读取号码或姓名，请手动输入。'
        : 'Could not auto-read the number or name from this file. Please enter them manually.',
      unsupported: lang === 'zh'
        ? '自动读取仅支持图片文件（JPG/PNG）。请手动输入，或重新上传清晰图片。'
        : 'Auto-read works for image files only (JPG/PNG). Enter the details manually or upload a clear photo.',
    };

    return (
      <p className={`input-hint ocr-status ocr-status--${status}`}>
        {messages[status]}
      </p>
    );
  };

  const handleGovernmentIdChange = (field, docType, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field.includes('NameOnId') ? value : formatGovernmentIdNumber(docType, value),
    }));
    if (ocrStatus[docType]) {
      setOcrStatus((prev) => ({ ...prev, [docType]: 'manual' }));
    }
  };

  const renderGovIdNameStatus = (nameField) => {
    const nameValue = formData[nameField];
    if (!nameValue) return null;
    const matches = govIdNameMatchesApplicant(formData, nameValue);
    return (
      <p className={`input-hint gov-id-name-status ${matches ? 'gov-id-name-status--ok' : 'gov-id-name-status--bad'}`}>
        {matches
          ? (lang === 'zh' ? '姓名与第一页登记姓名一致。' : 'Name matches your registered name from Step 1.')
          : (lang === 'zh' ? '姓名与第一页登记姓名不一致。' : 'Name does not match your registered name from Step 1.')}
      </p>
    );
  };

  const registeredApplicantName = buildApplicantDisplayName(formData);

  // BILINGUAL STEP BAR HEADERS & ACTION BUTTONS
  const stepsNames = lang === 'zh' ?
  [
    "个人信息", "教育背景", "工作经验", "联系方式与身份", "职位与法定信息", "工作模式与文件"
  ] : [
    "Personal Information", "Educational Background", "Work Experience", "Contact Identity", "Job & Statutory Details", "Work Set-Up & Documents"
  ];
  
  const headerText = lang === 'zh' ? "提交申请" : "Submit Application";
  const continueBtnText = lang === 'zh' ? "继续" : "Continue";
  const backBtnText = lang === 'zh' ? "返回" : "Back";

  const openDatePicker = (inputId) => {
    const input = document.getElementById(inputId);
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch {
        // Fall through to focus/click.
      }
    }
    input.focus();
    input.click();
  };

  const prevStep = () => {
    setFieldErrors({});
    setFormError('');
    setStep(step - 1);
  };

  const requiredFieldMsg = lang === 'zh' ? '必填' : 'Required';

  const isEmptyValue = (value) =>
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

  const clearFieldError = (...fields) => {
    setFieldErrors((prev) => {
      let changed = false;
      const next = { ...prev };
      fields.forEach((field) => {
        if (next[field]) {
          delete next[field];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const scrollAndHighlightField = (el, { showRequiredPopup = false } = {}) => {
    if (!el) return;

    const target =
      el.closest('.form-group, .form-field-group, .radio-checklist, .upload-box, .doc-upload-item') || el;

    document.querySelectorAll('.is-scroll-target').forEach((node) => {
      node.classList.remove('is-scroll-target');
    });
    target.classList.add('is-scroll-target');

    target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

    window.setTimeout(() => {
      try {
        if (typeof el.focus === 'function') {
          el.focus({ preventScroll: true });
        }
      } catch {
        // Ignore focus errors on non-focusable nodes.
      }

      if (showRequiredPopup && typeof el.reportValidity === 'function') {
        if (el.validity?.valueMissing || el.type === 'radio') {
          el.setCustomValidity(requiredFieldMsg);
        }
        el.reportValidity();
        el.setCustomValidity('');
      }
    }, 320);

    window.setTimeout(() => {
      target.classList.remove('is-scroll-target');
    }, 1800);
  };

  const focusFirstInvalidRequired = () => {
    const section = document.querySelector('.form-wrapper .form-section');
    if (!section) return false;

    const controls = section.querySelectorAll('input[required], select[required], textarea[required]');
    const seenRadioNames = new Set();

    for (const el of controls) {
      if (el.type === 'radio') {
        if (seenRadioNames.has(el.name)) continue;
        seenRadioNames.add(el.name);
        const checked = section.querySelector(`input[type="radio"][name="${el.name}"]:checked`);
        if (!checked) {
          scrollAndHighlightField(el, { showRequiredPopup: true });
          return true;
        }
        continue;
      }

      if (el.type === 'file') {
        if (!el.files || el.files.length === 0) {
          scrollAndHighlightField(el, { showRequiredPopup: true });
          return true;
        }
        continue;
      }

      if (!el.checkValidity()) {
        scrollAndHighlightField(el, { showRequiredPopup: true });
        return true;
      }
    }

    return false;
  };

  const validateStep = (currentStep) => {
    const errors = {};

    if (currentStep === 1) {
      const middleName = String(formData.middleName || '').trim();
      if (middleName !== '') {
        const letterCount = (middleName.match(/[A-Za-zÀ-ÖØ-öø-ÿ]/g) || []).length;
        if (letterCount < 2) {
          errors.middleName = lang === 'zh'
            ? '请填写完整中间名，不要只填写一个字母。'
            : 'Please enter your complete middle name, not just 1 letter.';
        }
      }
    }

    if (currentStep === 2 && isEmptyValue(formData.highestEducation)) {
      errors.highestEducation = requiredFieldMsg;
    }

    if (
      currentStep === 3 &&
      isEmptyValue(formData.bpoDurationLevel) &&
      isEmptyValue(formData.nonBpoCategory)
    ) {
      errors.bpoDurationLevel = requiredFieldMsg;
      errors.nonBpoCategory = requiredFieldMsg;
    }

    // Adapter still needs a duration choice selected.
    if (
      currentStep === 3 &&
      formData.nonBpoCategory === 'adapter' &&
      isEmptyValue(formData.bpoDurationLevel)
    ) {
      errors.bpoDurationLevel = requiredFieldMsg;
    }

    let topError = '';
    if (currentStep === 5) {
      const govValidation = validateGovernmentIdsForApplicant(formData);
      if (!govValidation.verified) {
        const govMsgs = getGovIdValidationErrors(govValidation);
        if (govMsgs.length) {
          topError = govMsgs.join('\n');
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError(topError);
      requestAnimationFrame(() => {
        const errorNode = document.querySelector('[data-field-error="true"]');
        const focusEl =
          errorNode?.closest('.form-group')?.querySelector('select, input, textarea, button') ||
          errorNode?.closest('.form-field-group')?.querySelector('button, select, input, textarea') ||
          errorNode;
        scrollAndHighlightField(focusEl || errorNode);
      });
      return false;
    }

    if (focusFirstInvalidRequired()) {
      setFieldErrors({});
      setFormError(topError);
      return false;
    }

    if (topError) {
      setFieldErrors({});
      setFormError(topError);
      requestAnimationFrame(() => {
        const alertNode = document.querySelector('.form-validation-alert, .requirement-alert');
        scrollAndHighlightField(alertNode);
      });
      return false;
    }

    setFieldErrors({});
    setFormError('');
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) setStep(step + 1);
  };

  const FieldError = ({ name }) => {
    const msg = fieldErrors[name];
    if (!msg) return null;
    return (
      <span className="field-error-msg" role="alert" data-field-error="true">
        {msg}
      </span>
    );
  };

  const validateRequirements = () => {
    const errors = [];

    const govValidation = validateGovernmentIdsForApplicant(formData);
    if (!govValidation.verified) {
      errors.push(...getGovIdValidationErrors(govValidation));
    }

    if (!fileUploads.resumeFiles.length) errors.push('Resume');

    return errors;
  };

  const handleChoice = (field, value) => {
    setFormError('');
    clearFieldError(field);
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'currentResidentialAddress' && prev.permanentSameAsCurrent) {
        next.permanentAddress = value;
      }
      return next;
    });
  };

  const handlePermanentSameAsCurrent = (checked) => {
    setFormError('');
    clearFieldError('permanentAddress');
    setFormData((prev) => ({
      ...prev,
      permanentSameAsCurrent: checked,
      permanentAddress: checked ? prev.currentResidentialAddress : prev.permanentAddress,
    }));
  };

  const syncExperienceFields = (bpoDurationLevel, nonBpoCategory) => {
    let experienceLevel = '';
    let bpoExperience = '';

    if (nonBpoCategory === 'starter') {
      experienceLevel = 'starter';
      bpoExperience = BPO_EXPERIENCE_CHOICES.FRESH_GRAD;
    } else if (nonBpoCategory === 'adapter') {
      experienceLevel = bpoDurationLevel || 'adapter';
      bpoExperience = BPO_EXPERIENCE_CHOICES.NON_BPO;
    } else if (bpoDurationLevel) {
      experienceLevel = bpoDurationLevel;
      bpoExperience = BPO_EXPERIENCE_CHOICES.YES;
    }

    return { experienceLevel, bpoExperience };
  };

  const handleBpoDurationSelect = (value) => {
    setFormError('');
    clearFieldError('bpoDurationLevel', 'nonBpoCategory');
    setFormData((prev) => {
      // Keep Adapter when choosing a duration; clear Starter / empty non-BPO otherwise.
      let nonBpoCategory = prev.nonBpoCategory;
      if (value) {
        if (nonBpoCategory === 'starter') {
          nonBpoCategory = '';
        } else if (nonBpoCategory !== 'adapter') {
          nonBpoCategory = '';
        }
      }
      const synced = syncExperienceFields(value, nonBpoCategory);
      return {
        ...prev,
        bpoDurationLevel: value,
        nonBpoCategory,
        ...synced,
      };
    });
  };

  const handleNonBpoCategorySelect = (value) => {
    setFormError('');
    clearFieldError('bpoDurationLevel', 'nonBpoCategory');
    setFormData((prev) => {
      // Starter locks duration (no experience). Adapter keeps duration open for selection.
      const bpoDurationLevel = value === 'starter' ? '' : prev.bpoDurationLevel;
      const synced = syncExperienceFields(bpoDurationLevel, value);
      return {
        ...prev,
        bpoDurationLevel,
        nonBpoCategory: value,
        ...synced,
      };
    });
  };

  const isNonBpoStarter = formData.nonBpoCategory === 'starter';
  const isBpoDurationLocked = isNonBpoStarter;
  const bpoDurationSelectValue = isNonBpoStarter
    ? 'starter-auto'
    : formData.bpoDurationLevel;

  const buildPayload = () => ({
    ...formData,
    resubmit_mode: applyMode === 'resubmit',

    // NEW MAPPED ALIASES BASED ON DATABASE REQUIREMENT
    first_name: formData.firstName,
    last_name: formData.lastName,
    middle_name: formData.middleName,
    suffix: formData.suffix,
    current_address: formData.currentResidentialAddress,
    permanent_address: formData.permanentAddress,
    city_municipality: formData.cityOrMunicipality,
    transparency_notes: formData.specialAccommodations,
    highest_educ: formData.highestEducation,
    school_name: formData.schoolName,
    year_graduated: formData.yearGraduated,
    bpoDurationLevel: formData.bpoDurationLevel,
    nonBpoCategory: formData.nonBpoCategory,
    experienceLevel: formData.experienceLevel || formData.bpoDurationLevel || formData.nonBpoCategory,
    experience_level: formData.experienceLevel || formData.bpoDurationLevel || formData.nonBpoCategory,
    bpoExperience: (() => {
      if (formData.nonBpoCategory === 'adapter' && formData.bpoDurationLevel) {
        return `${BPO_EXPERIENCE_CHOICES.NON_BPO} — Adapter — ${getExperienceLevelLabel(formData.bpoDurationLevel, lang)}`;
      }
      if (formData.bpoDurationLevel || formData.nonBpoCategory) {
        return `${formData.bpoExperience} — ${getExperienceLevelLabel(formData.bpoDurationLevel || formData.nonBpoCategory, lang)}`;
      }
      return formData.bpoExperience;
    })(),
    bpo_experience: (() => {
      if (formData.nonBpoCategory === 'adapter' && formData.bpoDurationLevel) {
        return `${BPO_EXPERIENCE_CHOICES.NON_BPO} — Adapter — ${getExperienceLevelLabel(formData.bpoDurationLevel, lang)}`;
      }
      if (formData.bpoDurationLevel || formData.nonBpoCategory) {
        return `${formData.bpoExperience} — ${getExperienceLevelLabel(formData.bpoDurationLevel || formData.nonBpoCategory, lang)}`;
      }
      return formData.bpoExperience;
    })(),
    latest_company: formData.latestCompanyName,
    total_years_work: formData.totalAllExperience,
    relevant_years_work: formData.totalRelevantExperience, 
    total_experience_desc: formData.totalExperience,
    reason_for_leaving: formData.reasonForLeaving,
    email: formData.email,
    mobile_number: formData.mobileNumber,
    dob: formData.dateOfBirth,
    tin_number: formData.tinNumber,
    tin_name_on_id: formData.tinNameOnId,
    sss_number: formData.sssNumber,
    sss_name_on_id: formData.sssNameOnId,
    philhealth_number: formData.philHealthNumber,
    philhealth_name_on_id: formData.philHealthNameOnId,
    pagibig_number: formData.pagIbigNumber,
    pagibig_name_on_id: formData.pagIbigNameOnId,
    gov_id_validation: validateGovernmentIdsForApplicant(formData),
    emergency_contact_name: formData.emergencyContactName,
    emergency_contact_relation: formData.emergencyContactRelation,
    emergency_contact_number: formData.emergencyContactMobile,
    gender: formData.gender,
    civil_status: formData.civilStatus,
    nationality: formData.nationality,
    religion: formData.religion,
    date_of_application: formData.dateOfApplication,
    source_referral: formData.sourceOfReferral,
    referrer_name: formData.referredBy,
    has_relative_in_helport: formData.hasRelativeHPBO,
    relative_name: formData.relativeHPBOName,
    relative_relation: formData.relativeHPBORelation,
    english_oral_proficiency: formData.englishOralSkills,
    english_written_proficiency: formData.englishWrittenSkills,
    other_languages: formData.otherLanguages,
    other_lang_written_proficiency: formData.otherLanguagesWrittenSkills,
    other_lang_oral_proficiency: formData.otherLanguagesOralSkills,
    resume_files: fileUploads.resumeFiles.map((file) => file.name).join(', '),
    birth_cert_files: fileUploads.birthCertificateFiles.map((file) => file.name).join(', '),
    tin_files: fileUploads.tinFiles.map((file) => file.name).join(', '),
    sss_files: fileUploads.sssFiles.map((file) => file.name).join(', '),
    philhealth_files: fileUploads.philHealthFiles.map((file) => file.name).join(', '),
    pagibig_files: fileUploads.pagIbigFiles.map((file) => file.name).join(', '),
    coe_files: fileUploads.coeFiles.map((file) => file.name).join(', '),
    nbi_files: fileUploads.nbiFiles.map((file) => file.name).join(', '),
  });

  // DATABASE CONNECTION: SUBMIT APPLICATION FORM
  const submitApplication = async (e) => {
    e.preventDefault();
    if (step !== 6) {
      nextStep();
      return;
    }

    const requirementErrors = validateRequirements();
    if (requirementErrors.length > 0) {
      setFormError(
        'Application not submitted: Please correct the required documents before submitting.\n- ' + requirementErrors.join('\n- ')
      );
      requestAnimationFrame(() => {
        if (!fileUploads.resumeFiles.length) {
          const resumeInput = document.querySelector('.form-wrapper .form-section input[type="file"][required]');
          scrollAndHighlightField(resumeInput, { showRequiredPopup: true });
          return;
        }
        const alertNode = document.querySelector('.form-wrapper .requirement-alert');
        scrollAndHighlightField(alertNode);
      });
      return;
    }

    setFormError('');

    const allUploads = [
      ...fileUploads.resumeFiles,
      ...fileUploads.birthCertificateFiles,
      ...fileUploads.tinFiles,
      ...fileUploads.sssFiles,
      ...fileUploads.philHealthFiles,
      ...fileUploads.pagIbigFiles,
      ...fileUploads.coeFiles,
      ...fileUploads.nbiFiles
    ];
    const totalUploadBytes = allUploads.reduce((sum, file) => sum + (file?.size || 0), 0);
    const maxUploadBytes = 38 * 1024 * 1024;
    if (totalUploadBytes > maxUploadBytes) {
      setFormError(
        'Application not submitted: Total upload size is too large. Please keep all attachments under 38MB combined and try again.'
      );
      return;
    }

    try {
      const submission = new FormData();
      submission.append('formData', JSON.stringify(buildPayload()));

      const appendFiles = (fieldName, files) => {
        (files || []).forEach((file) => submission.append(`${fieldName}[]`, file));
      };

      appendFiles('resume_files', fileUploads.resumeFiles);
      appendFiles('birth_cert_files', fileUploads.birthCertificateFiles);
      appendFiles('tin_files', fileUploads.tinFiles);
      appendFiles('sss_files', fileUploads.sssFiles);
      appendFiles('philhealth_files', fileUploads.philHealthFiles);
      appendFiles('pagibig_files', fileUploads.pagIbigFiles);
      appendFiles('coe_files', fileUploads.coeFiles);
      appendFiles('nbi_files', fileUploads.nbiFiles);

      const response = await fetch(`${API_URL}?action=submit_app`, {
        method: 'POST',
        body: submission
      });

      const rawText = await response.text();
      let data = null;
      try {
        data = JSON.parse(rawText);
      } catch (parseError) {
        console.error('Submit response parse error:', parseError, rawText);
        alert('Submission failed: Server returned an invalid response. Please check that Apache/MySQL are running and api.php is updated.');
        return;
      }

      if (data.status === 'success') {
        clearApplicationDraft();
        await cacheApplicantUploads(formData.email);
        if (typeof onSubmitSuccess === 'function') {
          onSubmitSuccess();
          return;
        }
        alert(lang === 'zh' ? "申请提交成功！" : "Application submitted successfully!");
        onBack();
      } else {
        const serverMessage = data.message || 'Unknown server error.';
        console.error('Submit failed:', serverMessage, data);
        if (data.code === 'already_submitted') {
          clearApplicationDraft();
          if (typeof onSubmitSuccess === 'function') {
            onSubmitSuccess();
            return;
          }
          alert(lang === 'zh'
            ? '您已提交过申请。请等待面试日期通知。'
            : 'You have already submitted an application. Please wait for your interview date.');
          onBack();
          return;
        }
        alert(`Submission failed: ${serverMessage}`);
      }
    } catch (error) {
      console.error("Submit error:", error);
      alert(`System error: ${error.message || 'Cannot connect to server.'}`);
    }
  };

  return (
    <div className="form-wrapper">
      <div className="form-card has-panel-close">
        <button type="button" className="close-form-btn form-close-btn" onClick={onBack} aria-label="Close form">&times;</button>

        <div className="form-card-header">
          <div className="apply-logo-wrap">
            <span className="apply-logo-ring" aria-hidden="true" />
            <span className="apply-logo-inner">
              <img src={hLogoImg} alt="Helport" className="apply-logo" />
            </span>
          </div>
          <p className="form-header-eyebrow">
            {lang === 'zh' ? '招聘申请' : 'Recruitment Portal'}
          </p>
          <h1 className="form-main-title">{headerText}</h1>
          <div className="form-step-badge">
            <span className="form-step-num">
              {lang === 'zh' ? `第 ${step} 步，共 6 步` : `Step ${step} of 6`}
            </span>
            <span className="form-step-sep" aria-hidden="true" />
            <span className="form-step-name">{stepsNames[step - 1]}</span>
          </div>
        </div>

        <div className="step-progress-track" aria-hidden="true">
          <div className="step-progress-fill" style={{ width: `${(step / 6) * 100}%` }} />
        </div>

        <div className="stepper-top-bar">
          {stepsNames.map((name, index) => (
            <div
              key={index}
              className={`stepper-item${step > index + 1 ? ' completed' : ''}${step === index + 1 ? ' active' : ''}`}
            >
              <div className="stepper-circle">{step > index + 1 ? '✓' : index + 1}</div>
              <div className="stepper-label">{name}</div>
            </div>
          ))}
        </div>

        <form onSubmit={submitApplication}>
          {formError && step !== 6 && (
            <div className="requirement-alert form-validation-alert">
              {formError.split('\n').map((line, idx) => (
                <div key={idx}>{line}</div>
              ))}
            </div>
          )}
          
          {/* STEP 1: Personal Information & Eligibility */}
          {step === 1 && (
            <div className="form-section">
              <h3>{lang === 'zh' ? "个人信息" : "Personal Information"}</h3>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '资格' : 'Eligibility'}</p>
                <div className="input-grid">
                  <div className="form-group">
                    <label>{lang === 'zh' ? "是否未满 18 周岁？ *" : "Are you less than 18 years of age? *"}</label>
                    <p className="input-hint input-hint-muted">{lang === 'zh' ? 'HPBO 不允许未成年人工作。' : 'No minor will be allowed to work at HPBO.'}</p>
                    <select 
                      className="form-select" 
                      value={formData.isMinor} 
                      onChange={(e) => handleChoice('isMinor', e.target.value)} 
                      required
                    >
                      <option value="" disabled>Choose</option>
                      <option value="Yes, I am.">Yes, I am.</option>
                      <option value="No, I am not.">No, I am not.</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{lang === 'zh' ? "出生日期 *" : "Date of Birth *"}</label>
                    <div className="date-input-wrap">
                      <input
                        id="apply-date-of-birth"
                        type="date"
                        className="form-input date-input-field"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleChoice('dateOfBirth', e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="date-input-icon-btn"
                        onClick={() => openDatePicker('apply-date-of-birth')}
                        aria-label={lang === 'zh' ? '选择出生日期' : 'Open date of birth calendar'}
                        title={lang === 'zh' ? '选择日期' : 'Pick date'}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" stroke="currentColor" strokeWidth="2" />
                          <path d="M3.5 9.5h17" stroke="currentColor" strokeWidth="2" />
                          <path d="M8 3.5v3.5M16 3.5v3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          <path d="M8.5 13h2M13.5 13h2M8.5 16.5h2M13.5 16.5h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '全名' : 'Full Name'}</p>
                <div className="input-grid">
                  <div className="form-group"><label>{lang === 'zh' ? "名字 *" : "First Name *"}</label><input type="text" placeholder="Juan" value={formData.firstName} onChange={(e) => handleChoice('firstName', e.target.value)} required /></div>
                  <div className="form-group"><label>{lang === 'zh' ? "姓氏 *" : "Last Name *"}</label><input type="text" placeholder="Dela Cruz" value={formData.lastName} onChange={(e) => handleChoice('lastName', e.target.value)} required /></div>
                  <div className="form-group">
                    <label>{lang === 'zh' ? "中间名" : "Middle Name"}</label>
                    <input
                      type="text"
                      placeholder={lang === 'zh' ? "请填写完整中间名" : "Complete Middle Name"}
                      value={formData.middleName}
                      onChange={(e) => handleChoice('middleName', e.target.value)}
                      minLength={2}
                    />
                    <FieldError name="middleName" />
                  </div>
                  <div className="form-group"><label>{lang === 'zh' ? "后缀" : "Suffix"}</label><input type="text" placeholder="Optional" value={formData.suffix} onChange={(e) => handleChoice('suffix', e.target.value)} /></div>
                </div>
              </div>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '地址' : 'Address'}</p>
                <div className="form-group">
                  <label>{lang === 'zh' ? "当前居住地址 *" : "Current Residential Address *"}</label>
                  <textarea placeholder="Complete address" value={formData.currentResidentialAddress} onChange={(e) => handleChoice('currentResidentialAddress', e.target.value)} required></textarea>
                </div>
                <div className="form-group">
                  <label>{lang === 'zh' ? "永久地址 *" : "Permanent Address *"}</label>
                  <label className="address-same-option">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.permanentSameAsCurrent)}
                      onChange={(e) => handlePermanentSameAsCurrent(e.target.checked)}
                    />
                    <span>{lang === 'zh' ? '与当前居住地址相同' : 'Same as current residential address'}</span>
                  </label>
                  <textarea
                    placeholder={formData.permanentSameAsCurrent
                      ? (lang === 'zh' ? '将自动使用当前居住地址' : 'Uses your current residential address automatically')
                      : (lang === 'zh' ? '请输入完整永久地址' : 'Enter your complete permanent address')}
                    value={formData.permanentAddress}
                    onChange={(e) => handleChoice('permanentAddress', e.target.value)}
                    readOnly={formData.permanentSameAsCurrent}
                    required
                  ></textarea>
                </div>
                <div className="form-group">
                  <label>{lang === 'zh' ? "城市或自治市 *" : "City or Municipality *"}</label>
                  <select 
                    className="form-select" 
                    value={formData.cityOrMunicipality} 
                    onChange={(e) => handleChoice('cityOrMunicipality', e.target.value)} 
                    required
                  >
                  <option value="" disabled>Choose City/Municipality</option>
                  <optgroup label="Metro Manila (NCR)">
                    <option value="Caloocan City">Caloocan City</option>
                    <option value="Las Piñas City">Las Piñas City</option>
                    <option value="Makati City">Makati City</option>
                    <option value="Malabon City">Malabon City</option>
                    <option value="Mandaluyong City">Mandaluyong City</option>
                    <option value="Manila City">Manila City</option>
                    <option value="Marikina City">Marikina City</option>
                    <option value="Muntinlupa City">Muntinlupa City</option>
                    <option value="Navotas City">Navotas City</option>
                    <option value="Parañaque City">Parañaque City</option>
                    <option value="Pasay City">Pasay City</option>
                    <option value="Pasig City">Pasig City</option>
                    <option value="Pateros">Pateros</option>
                    <option value="Quezon City">Quezon City</option>
                    <option value="San Juan City">San Juan City</option>
                    <option value="Taguig City">Taguig City</option>
                    <option value="Valenzuela City">Valenzuela City</option>
                  </optgroup>
                  <optgroup label="Provincial Areas">
                    <option value="Bulacan">Bulacan</option>
                    <option value="Cavite">Cavite</option>
                    <option value="Laguna">Laguna</option>
                    <option value="Rizal">Rizal</option>
                    <option value="Other Provincial City/Municipality">Other Provincial City / Municipality</option>
                  </optgroup>
                </select>
                </div>
              </div>

              <div className="form-field-group wide-section">
                <p className="form-field-group-title">{lang === 'zh' ? '特殊便利 / 透明度' : 'Accommodations & Transparency'}</p>
                <p className="input-hint input-hint-muted">
                  {lang === 'zh' ?
                    "Helport 遵守非歧视与平等就业机会原则，并为有特殊需求者提供合理便利。请选择最适用于您的一项：" :
                    "Helport upholds non-discrimination and equal opportunity. Select the option most applicable to you:"}
                </p>
                
                <div className="radio-checklist">
                  {[
                    "I am a Solo Parent.",
                    "I am a Person with disability (PWD).",
                    "Soon to avail Maternity Leave Benefits.",
                    "I have autoimmune health conditions.",
                    "I am a transgender.",
                    "Mental Health Support programs.",
                    "I have pending civil or criminal case filed against me.",
                    "I have a sensitive pregnancy.",
                    "I have active outdoor sports lifestyle.",
                    "I am a small-time business owner.",
                    "I am a Christian and I look for a bible study group.",
                    "I am a Muslim and may need a dedicated place of worship whenever possible.",
                    "I have credit cards or loan companies looking for me for an unsettled loan.",
                    "I am a working student.",
                    "I have a part-time job.",
                    "I cannot work on a night shift.",
                    "I am retired from my previous job role.",
                    "I am a reserved officer of PNP or AFP.",
                    "I am single.",
                    "I am a bread winner.",
                    "I live alone in Metro Manila.",
                    "I am a musician, and I have strong passion for music.",
                    "I am an introvert, and I may have difficulty initiating conversation with people I just met."
                  ].map((option) => (
                    <label key={option}>
                      <input 
                        type="radio" 
                        name="specialAccommodations" 
                        value={option}
                        checked={formData.specialAccommodations === option}
                        onChange={(e) => handleChoice('specialAccommodations', e.target.value)}
                        required
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="action-btns">
                <button type="button" className="btn-next" onClick={nextStep}>{continueBtnText}</button>
              </div>
            </div>
          )}

          {/* STEP 2: Educational Background */}
          {step === 2 && (
            <div className="form-section">
              <h3>{lang === 'zh' ? "教育背景" : "Educational Background"}</h3>
              <div className={`form-field-group${fieldErrors.highestEducation ? ' has-field-error' : ''}`}>
                <p className="form-field-group-title">{lang === 'zh' ? "最高学历 *" : "Highest Educational Attainment *"}</p>
                <div className="choice-container cols-6">
                  {['High School Graduate (Old Curriculum)', 'Senior High School Graduate', 'College Undergraduate', 'College Graduate', 'Vocational Course Graduate', 'Masteral / Doctorate Degree'].map((option) => (
                    <button key={option} type="button" className={`choice-btn ${formData.highestEducation === option ? 'active' : ''}`} onClick={() => handleChoice('highestEducation', option)}>{option}</button>
                  ))}
                </div>
                <FieldError name="highestEducation" />
              </div>
              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '学校信息' : 'School Details'}</p>
                <div className="input-grid">
                  <div className="form-group">
                    <label>{lang === 'zh' ? "学校名称 *" : "School Name *"}</label>
                    <input type="text" placeholder="e.g. Rizal Technological University" value={formData.schoolName} onChange={(e) => handleChoice('schoolName', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label>{lang === 'zh' ? "毕业年份 *" : "Year Graduated *"}</label>
                    <input type="number" placeholder="YYYY" value={formData.yearGraduated} onChange={(e) => handleChoice('yearGraduated', e.target.value)} required />
                  </div>
                </div>
              </div>
              <div className="action-btns">
                <button type="button" className="btn-back" onClick={prevStep}>{backBtnText}</button>
                <button type="button" className="btn-next" onClick={nextStep}>{continueBtnText}</button>
              </div>
            </div>
          )}

          {/* STEP 3: Work Experience */}
          {step === 3 && (
            <div className="form-section">
              <h3>{lang === 'zh' ? "工作经验" : "Work Experience"}</h3>
              <p className="input-hint input-hint-muted form-experience-intro">
                {lang === 'zh'
                  ? '有 BPO 经验请选择上方年限；无 BPO 经验请选择下方类别。选择 Starter 将禁用上方年限；选择 Adapter 后仍需选择上方年限。'
                  : 'If you have BPO experience, choose a duration above. If not, choose a category below. Starter disables the duration field; Adapter still requires choosing a duration above.'}
              </p>
              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? 'BPO 工作年限' : 'BPO Experience Duration'}</p>
                <div className={`form-group${fieldErrors.bpoDurationLevel ? ' has-error' : ''}${isBpoDurationLocked ? ' is-disabled-field' : ''}`}>
                  <label>{lang === 'zh' ? '请选择您的 BPO 经验年限' : 'Select how long your BPO experience is'}</label>
                  <select
                    className="form-select"
                    value={bpoDurationSelectValue}
                    onChange={(e) => handleBpoDurationSelect(e.target.value)}
                    disabled={isBpoDurationLocked}
                  >
                    <option value="">{lang === 'zh' ? '请选择...' : 'Select duration...'}</option>
                    {isNonBpoStarter && (
                      <option value="starter-auto">
                        {lang === 'zh' ? '不适用 — 完全没有工作经验' : 'N/A — No experience at all'}
                      </option>
                    )}
                    {BPO_DURATION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {lang === 'zh' ? option.labelZh : option.label} — {option.tier}
                      </option>
                    ))}
                  </select>
                  <FieldError name="bpoDurationLevel" />
                </div>
              </div>

              <p className="form-experience-or" aria-hidden="true">{lang === 'zh' ? '或' : 'OR'}</p>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '无 BPO 经验类别' : 'No BPO Experience Category'}</p>
                <div className={`form-group${fieldErrors.nonBpoCategory ? ' has-error' : ''}`}>
                  <label>{lang === 'zh' ? '如果没有 BPO 经验，请选择以下选项' : 'If you have no BPO experience, select one of the following'}</label>
                  <select
                    className="form-select"
                    value={formData.nonBpoCategory}
                    onChange={(e) => handleNonBpoCategorySelect(e.target.value)}
                  >
                    <option value="">{lang === 'zh' ? '请选择...' : 'Select category...'}</option>
                    {NON_BPO_EXPERIENCE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} — {lang === 'zh' ? option.descZh : option.desc}
                      </option>
                    ))}
                  </select>
                  <FieldError name="nonBpoCategory" />
                </div>
              </div>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '工作年限' : 'Years of Experience'}</p>
                <div className="form-group">
                  <label>{lang === 'zh' ? "最新公司名称" : "Latest Company Name"}</label>
                  <input type="text" placeholder="Leave blank if none" value={formData.latestCompanyName} onChange={(e) => handleChoice('latestCompanyName', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>{lang === 'zh' ? "总工作年限 *" : "Total Years of ALL Work Experience *"}</label>
                  <p className="input-hint input-hint-muted">{lang === 'zh' ? '自您开始工作以来的所有工作年限。' : 'All work experience since you started working.'}</p>
                  <input 
                    type="text" 
                    placeholder="e.g. 3 years" 
                    value={formData.totalAllExperience} 
                    onChange={(e) => handleChoice('totalAllExperience', e.target.value)} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>{lang === 'zh' ? "相关工作经验年限 *" : "Total RELEVANT Work Experience *"}</label>
                  <p className="input-hint input-hint-muted">{lang === 'zh' ? '与您在 Helport 申请的职位相关的经验。' : 'Experience related to the position you are applying for at Helport.'}</p>
                  <input 
                    type="text" 
                    placeholder="e.g. 2 years" 
                    value={formData.totalRelevantExperience} 
                    onChange={(e) => handleChoice('totalRelevantExperience', e.target.value)} 
                    required 
                  />
                </div>
                <div className="input-grid">
                  <div className="form-group"><label>{lang === 'zh' ? '经验描述' : 'Experience Summary'}</label><input type="text" placeholder="e.g. 2 years" value={formData.totalExperience} onChange={(e) => handleChoice('totalExperience', e.target.value)} /></div>
                  <div className="form-group"><label>{lang === 'zh' ? '离职原因' : 'Reason for Leaving'}</label><input type="text" placeholder="e.g. Career Growth" value={formData.reasonForLeaving} onChange={(e) => handleChoice('reasonForLeaving', e.target.value)} /></div>
                </div>
              </div>
              <div className="action-btns">
                <button type="button" className="btn-back" onClick={prevStep}>{backBtnText}</button>
                <button type="button" className="btn-next" onClick={nextStep}>{continueBtnText}</button>
              </div>
            </div>
          )}

          {/* STEP 4: Contact & Identity */}
          {step === 4 && (
            <div className="form-section">
              <h3>{lang === 'zh' ? "联系方式与身份" : "Contact & Identity"}</h3>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '联系方式' : 'Contact Details'}</p>
                <div className="input-grid">
                  <div className="form-group"><label>{lang === 'zh' ? "电子邮箱 *" : "E-mail *"}</label><input type="email" value={formData.email} onChange={(e) => handleChoice('email', e.target.value)} required /></div>
                  <div className="form-group"><label>{lang === 'zh' ? "手机号码 *" : "Mobile Number *"}</label><input type="tel" value={formData.mobileNumber} onChange={(e) => handleChoice('mobileNumber', e.target.value)} required /></div>
                </div>
              </div>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '紧急联系人' : 'Emergency Contact'}</p>
                <div className="input-grid">
                  <div className="form-group">
                    <label>{lang === 'zh' ? "联系人姓名 *" : "Contact Person Name *"}</label>
                    <input 
                      type="text" 
                      placeholder="Full name" 
                      value={formData.emergencyContactName}
                      onChange={(e) => handleChoice('emergencyContactName', e.target.value)}
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>{lang === 'zh' ? "关系 *" : "Relationship *"}</label>
                    <select 
                      className="form-select" 
                      value={formData.emergencyContactRelation} 
                      onChange={(e) => handleChoice('emergencyContactRelation', e.target.value)} 
                      required
                    >
                  <option value="" disabled>Choose</option>
                  <option value="Spouse (Married)">Spouse (Married)</option>
                  <option value="Partner (Not Married)">Partner (Not Married)</option>
                  <option value="Mother">Mother</option>
                  <option value="Father">Father</option>
                  <option value="Sister">Sister</option>
                  <option value="Brother">Brother</option>
                  <option value="Grand Parent">Grand Parent</option>
                  <option value="Auntie">Auntie</option>
                  <option value="Uncle">Uncle</option>
                  <option value="Cousin">Cousin</option>
                  <option value="Relative / Long distant Relative">Relative / Long distant Relative</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Close Friend / Family Friend">Close Friend / Family Friend</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>{lang === 'zh' ? "手机号码 *" : "Mobile Number *"}</label>
                  <p className="input-hint input-hint-muted">Format: 09XX-XXXXXXX</p>
                  <input 
                    type="tel" 
                    placeholder="09XX-XXXXXXX" 
                    value={formData.emergencyContactMobile}
                    onChange={(e) => handleChoice('emergencyContactMobile', e.target.value)}
                    required 
                  />
                </div>
              </div>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '个人信息' : 'Personal Details'}</p>
                <div className="input-grid-3">
                  <div className="form-group">
                    <label>{lang === 'zh' ? "性别 *" : "Gender *"}</label>
                    <select className="form-select" value={formData.gender} onChange={(e) => handleChoice('gender', e.target.value)} required>
                      <option value="" disabled>Choose</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{lang === 'zh' ? "婚姻状况 *" : "Civil Status *"}</label>
                    <select className="form-select" value={formData.civilStatus} onChange={(e) => handleChoice('civilStatus', e.target.value)} required>
                      <option value="" disabled>Choose</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Widowed">Widowed</option>
                      <option value="Separated">Separated</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{lang === 'zh' ? "国籍 *" : "Nationality *"}</label>
                    <select className="form-select" value={formData.nationality} onChange={(e) => handleChoice('nationality', e.target.value)} required>
                      <option value="" disabled>Choose</option>
                      <option value="Filipino">Filipino</option>
                      <option value="Non-Filipino">Non-Filipino</option>
                    </select>
                  </div>
                </div>
                <div className="input-grid">
                  <div className="form-group">
                    <label>{lang === 'zh' ? "宗教信仰 *" : "Religion *"}</label>
                    <select className="form-select" value={formData.religion} onChange={(e) => handleChoice('religion', e.target.value)} required>
                  <option value="" disabled>Choose</option>
                  <option value="Aglipay (Iglesia Filipina Independiente)">Aglipay (Iglesia Filipina Independiente)</option>
                  <option value="Christian">Christian</option>
                  <option value="Iglesia Ni Cristo">Iglesia Ni Cristo</option>
                  <option value="Roman Catholic">Roman Catholic</option>
                  <option value="Anglican">Anglican</option>
                  <option value="Buddhist">Buddhist</option>
                  <option value="Taoist">Taoist</option>
                  <option value="Muslim">Muslim</option>
                  <option value="Mormons">Mormons</option>
                  <option value="Jehovah's Witnesses">Jehovah's Witnesses</option>
                  <option value="Atheist">Atheist</option>
                  <option value="Others">Others</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{lang === 'zh' ? "申请日期 *" : "Date of Application *"}</label>
                    <div className="date-input-wrap">
                      <input
                        id="apply-date-of-application"
                        type="date"
                        className="form-input date-input-field"
                        value={formData.dateOfApplication}
                        onChange={(e) => handleChoice('dateOfApplication', e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="date-input-icon-btn"
                        onClick={() => openDatePicker('apply-date-of-application')}
                        aria-label={lang === 'zh' ? '选择申请日期' : 'Open date of application calendar'}
                        title={lang === 'zh' ? '选择日期' : 'Pick date'}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" stroke="currentColor" strokeWidth="2" />
                          <path d="M3.5 9.5h17" stroke="currentColor" strokeWidth="2" />
                          <path d="M8 3.5v3.5M16 3.5v3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          <path d="M8.5 13h2M13.5 13h2M8.5 16.5h2M13.5 16.5h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '推荐来源' : 'Referral'}</p>
                <div className="input-grid">
                  <div className="form-group">
                    <label>{lang === 'zh' ? "推荐来源 *" : "Source of Referral *"}</label>
                    <select className="form-select" value={formData.sourceOfReferral} onChange={(e) => handleChoice('sourceOfReferral', e.target.value)} required>
                  <option value="" disabled>Choose</option>
                  <option value="Employee Referral">Employee Referral</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Glassdoor">Glassdoor</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Recruitment Agency">Recruitment Agency</option>
                  <option value="Reddit">Reddit</option>
                  <option value="Walk in">Walk in</option>
                  <option value="Word of Mouth">Word of Mouth</option>
                  <option value="Others">Others</option>
                  <option value="None">None</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{lang === 'zh' ? "推荐人姓名 *" : "Referred By (Employee Name) *"}</label>
                    <p className="input-hint input-hint-muted">Write &quot;N/A&quot; if not applicable</p>
                    <input type="text" placeholder="Employee name or N/A" className="form-input" value={formData.referredBy} onChange={(e) => handleChoice('referredBy', e.target.value)} required />
                  </div>
                </div>
              </div>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? 'HPBO 亲属' : 'Relative at HPBO'}</p>
                <div className="form-group">
                  <label>{lang === 'zh' ? '是否有亲属在 HPBO 工作？ *' : 'Do you have a relative working at HPBO? *'}</label>
                  <select className="form-select" value={formData.hasRelativeHPBO} onChange={(e) => handleChoice('hasRelativeHPBO', e.target.value)} required>
                    <option value="" disabled>Choose</option>
                    <option value="Yes, I have.">Yes, I have.</option>
                    <option value="None">None</option>
                  </select>
                </div>
                <div className="input-grid">
                  <div className="form-group">
                    <label>{lang === 'zh' ? '亲属姓名 *' : 'Relative Name *'}</label>
                    <p className="input-hint input-hint-muted">Write &quot;Not Applicable&quot; if none</p>
                    <input type="text" placeholder="Name or N/A" className="form-input" value={formData.relativeHPBOName} onChange={(e) => handleChoice('relativeHPBOName', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label>{lang === 'zh' ? '关系 *' : 'Relationship *'}</label>
                    <select className="form-select" value={formData.relativeHPBORelation} onChange={(e) => handleChoice('relativeHPBORelation', e.target.value)} required>
                  <option value="" disabled>Choose</option>
                  <option value="Spouse (for married person)">Spouse (for married person)</option>
                  <option value="Partner (not married)">Partner (not married)</option>
                  <option value="Boyfriend/Girlfriend (not married)">Boyfriend/Girlfriend (not married)</option>
                  <option value="Sibling (brother or sister)">Sibling (brother or sister)</option>
                  <option value="Cousin">Cousin</option>
                  <option value="Uncle">Uncle</option>
                  <option value="Auntie">Auntie</option>
                  <option value="Parent (mother or father)">Parent (mother or father)</option>
                  <option value="Long distant relative">Long distant relative</option>
                  <option value="Your friend">Your friend</option>
                  <option value="Friend of Friend">Friend of Friend</option>
                  <option value="No Relative">No Relative</option>
                  <option value="Not Applicable">Not Applicable</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '语言技能' : 'Language Skills'}</p>
              <div className="form-group rating-group">
                <label><strong>English Oral Skills</strong> — Rate yourself 1 to 10 *</label>
                <p className="input-hint input-hint-muted">Rate yourself from 1 to 10. 10 as the highest while 1 as the lowest.</p>
                <div className="rating-scale">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <div key={`oral-${num}`} className="rating-scale-item">
                      <span className="rating-scale-num">{num}</span>
                      <input 
                        type="radio" 
                        name="englishOralSkills" 
                        value={num} 
                        checked={parseInt(formData.englishOralSkills) === num}
                        onChange={(e) => handleChoice('englishOralSkills', e.target.value)}
                        required 
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group rating-group">
                <label><strong>English Written Skills</strong> — Rate yourself 1 to 10 *</label>
                <p className="input-hint input-hint-muted">Rate yourself from 1 to 10. 10 as the highest while 1 as the lowest.</p>
                <div className="rating-scale">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <div key={`written-${num}`} className="rating-scale-item">
                      <span className="rating-scale-num">{num}</span>
                      <input 
                        type="radio" 
                        name="englishWrittenSkills" 
                        value={num} 
                        checked={parseInt(formData.englishWrittenSkills) === num}
                        onChange={(e) => handleChoice('englishWrittenSkills', e.target.value)}
                        required 
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>{lang === 'zh' ? "其他语言 *" : "Other Languages *"}</label>
                <p className="input-hint input-hint-muted">{lang === 'zh' ? "如无其他语言，请输入 'English and/or Tagalog only'" : 'If none, write "English and/or Tagalog only"'}</p>
                <input 
                  type="text" 
                  placeholder="Your answer" 
                  className="form-input"
                  value={formData.otherLanguages} 
                  onChange={(e) => handleChoice('otherLanguages', e.target.value)} 
                  required 
                />
              </div>

              <div className="input-grid">
                <div className="form-group">
                  <label><strong>Other Languages — Written Skills *</strong></label>
                  <select 
                    className="form-select" 
                    value={formData.otherLanguagesWrittenSkills} 
                    onChange={(e) => handleChoice('otherLanguagesWrittenSkills', e.target.value)} 
                    required
                  >
                    <option value="" disabled>Choose</option>
                    <option value="Elementary or Beginner Level">Elementary or Beginner Level</option>
                    <option value="Intermediate Level">Intermediate Level</option>
                    <option value="Advance Level">Advance Level</option>
                    <option value="Not Applicable">Not Applicable</option>
                  </select>
                </div>
                <div className="form-group">
                  <label><strong>Other Languages — Oral Skills *</strong></label>
                  <select 
                    className="form-select" 
                    value={formData.otherLanguagesOralSkills} 
                    onChange={(e) => handleChoice('otherLanguagesOralSkills', e.target.value)} 
                    required
                  >
                    <option value="" disabled>Choose</option>
                    <option value="Elementary or Beginner Level">Elementary or Beginner Level</option>
                    <option value="Intermediate Level">Intermediate Level</option>
                    <option value="Advance Level">Advance Level</option>
                    <option value="Not Applicable">Not Applicable</option>
                  </select>
                </div>
              </div>
              </div>

              <div className="action-btns">
                <button type="button" className="btn-back" onClick={prevStep}>{backBtnText}</button>
                <button type="button" className="btn-next" onClick={nextStep}>{continueBtnText}</button>
              </div>
            </div>
          )}

          {/* STEP 5: Position & Statutory */}
          {step === 5 && (
            <div className="form-section">
              <h3>{lang === 'zh' ? "职位与法定信息" : "Job & Statutory Details"}</h3>
              
              <div className="doc-warning-banner">
                <div className="doc-warning-title">
                  ⚠️ {lang === 'zh' ? "必需文件" : "Required Documents"}
                </div>
                <p>
                  {lang === 'zh' 
                    ? "以下文件是完成您的申请所必需的。如果任何信息不完整或不正确，您的申请将被暂停。" 
                    : "The following documents are required to complete your application. Your salary will be put on-hold if any information is missing or incorrect:"}
                </p>
                <ul>
                  <li>{lang === 'zh' ? "简历" : "Resume / CV"}</li>
                </ul>
                <p className="doc-warning-optional">
                  {lang === 'zh'
                    ? "选填项：PSA/出生证明、COE/离职证明、NBI 无犯罪证明，以及 SSS、TIN、PhilHealth 和 Pag-IBIG 号码及文件可稍后提交。"
                    : "Optional: PSA/Birth Certificate, COE/Clearance, NBI Clearance, and SSS, TIN, PhilHealth, and Pag-IBIG numbers and file uploads may be submitted later."}
                </p>
              </div>
              
              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '申请职位' : 'Position Applied For'}</p>
                <div className="form-group">
                  <label>{lang === 'zh' ? 'Helport PH 申请职位 *' : 'Position pursuing with Helport PH *'}</label>
                  <select className="form-select" value={formData.position} onChange={(e) => handleChoice('position', e.target.value)} required>
                    <option value="" disabled>Choose</option>
                    {['Accounting Staff / Accounting Officer', 'Admin Manager', 'Assistant Manager / Operations Manager', 'Business Developer Manager', 'Content Moderator', 'Customer Service Representative', 'Data Labeler', 'Debt Collection Agent (Local)', 'HR Specialist / HR Generalist', 'IT Support', 'Operations Supervisor', 'Outbound Sales Agent', 'Project Manager', 'QA Specialist / Analist', 'Recruiter', 'Software Specialist', 'Team Leader', 'Trainer', 'Executive Assistant / Executive Secretary', 'Others'].map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-field-group">
                <p className="form-field-group-title">
                  {lang === 'zh' ? '政府 ID 号码（选填）' : 'Government ID Numbers (Optional)'}
                </p>
                <div className="gov-id-auto-notice">
                  <p>
                    {lang === 'zh'
                      ? `仅 SSS、TIN、PhilHealth 和 Pag-IBIG 号码及文件为选填项。若填写，请同时填写证件号码及证件上显示的姓名，且姓名须与第一页登记的姓名一致：${registeredApplicantName || '（请先完成第一页）'}。`
                      : `Only SSS, TIN, PhilHealth, and Pag-IBIG numbers and file uploads are optional. If provided, enter each ID number and the full name shown on the ID. The name must match your registered name from Step 1: ${registeredApplicantName || '(complete Step 1 first)'}.`}
                  </p>
                </div>
                <div className="statutory-grid">
                  <div className="form-group">
                    <label>Tax Identification Number (TIN) <span className="field-optional">(Optional)</span></label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9\s\-]+"
                      placeholder="Enter digits only"
                      value={formData.tinNumber}
                      onChange={(e) => handleGovernmentIdChange('tinNumber', 'tin', e.target.value)}
                    />
                    <label className="gov-id-name-label">Full name as shown on TIN ID</label>
                    <input
                      type="text"
                      placeholder="Must match your Step 1 name"
                      value={formData.tinNameOnId}
                      onChange={(e) => handleGovernmentIdChange('tinNameOnId', 'tin', e.target.value)}
                    />
                    {renderGovIdNameStatus('tinNameOnId')}
                  </div>

                  <div className="form-group">
                    <label>SSS No. <span className="field-optional">(Optional)</span></label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9\s\-]+"
                      placeholder="Enter digits only"
                      value={formData.sssNumber}
                      onChange={(e) => handleGovernmentIdChange('sssNumber', 'sss', e.target.value)}
                    />
                    <label className="gov-id-name-label">Full name as shown on SSS ID</label>
                    <input
                      type="text"
                      placeholder="Must match your Step 1 name"
                      value={formData.sssNameOnId}
                      onChange={(e) => handleGovernmentIdChange('sssNameOnId', 'sss', e.target.value)}
                    />
                    {renderGovIdNameStatus('sssNameOnId')}
                  </div>

                  <div className="form-group">
                    <label>Phil Health No. <span className="field-optional">(Optional)</span></label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9\s\-]+"
                      placeholder="Enter digits only"
                      value={formData.philHealthNumber}
                      onChange={(e) => handleGovernmentIdChange('philHealthNumber', 'philhealth', e.target.value)}
                    />
                    <label className="gov-id-name-label">Full name as shown on PhilHealth ID</label>
                    <input
                      type="text"
                      placeholder="Must match your Step 1 name"
                      value={formData.philHealthNameOnId}
                      onChange={(e) => handleGovernmentIdChange('philHealthNameOnId', 'philhealth', e.target.value)}
                    />
                    {renderGovIdNameStatus('philHealthNameOnId')}
                  </div>

                  <div className="form-group">
                    <label>Pag-IBIG No. <span className="field-optional">(Optional)</span></label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9\s\-]+"
                      placeholder="Enter digits only"
                      value={formData.pagIbigNumber}
                      onChange={(e) => handleGovernmentIdChange('pagIbigNumber', 'pagibig', e.target.value)}
                    />
                    <label className="gov-id-name-label">Full name as shown on Pag-IBIG ID</label>
                    <input
                      type="text"
                      placeholder="Must match your Step 1 name"
                      value={formData.pagIbigNameOnId}
                      onChange={(e) => handleGovernmentIdChange('pagIbigNameOnId', 'pagibig', e.target.value)}
                    />
                    {renderGovIdNameStatus('pagIbigNameOnId')}
                  </div>
                </div>
              </div>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '当前就业' : 'Current Employment'}</p>
              <div className="form-group">
                <label>Are you currently employed? *</label>
                <select 
                  className="form-select" 
                  value={formData.isCurrentlyEmployed} 
                  onChange={(e) => {
                    const value = e.target.value;
                    const notEmployed = value === 'No, I am not employed.';
                    setFormError('');
                    clearFieldError(
                      'isCurrentlyEmployed',
                      'currentEmployer',
                      'currentRole',
                      'currentEmploymentPeriod'
                    );
                    setFormData((prev) => ({
                      ...prev,
                      isCurrentlyEmployed: value,
                      currentEmployer: notEmployed ? 'N/A' : (prev.currentEmployer === 'N/A' ? '' : prev.currentEmployer),
                      currentRole: notEmployed ? 'N/A' : (prev.currentRole === 'N/A' ? '' : prev.currentRole),
                      currentEmploymentPeriod: notEmployed
                        ? 'N/A'
                        : (prev.currentEmploymentPeriod === 'N/A' ? '' : prev.currentEmploymentPeriod),
                    }));
                  }} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I am currently employed.">Yes, I am currently employed.</option>
                  <option value="Yes, I am still employed but I am now rendering my resignation.">Yes, I am still employed but I am now rendering my resignation.</option>
                  <option value="No, I am not employed.">No, I am not employed.</option>
                </select>
              </div>

              <div className={`form-group${formData.isCurrentlyEmployed === 'No, I am not employed.' ? ' is-disabled-field' : ''}`}>
                <label>If currently employed, what is the name of your Employer or Company?{formData.isCurrentlyEmployed !== 'No, I am not employed.' ? ' *' : ''}</label>
                <input 
                  type="text" 
                  placeholder={formData.isCurrentlyEmployed === 'No, I am not employed.' ? 'N/A' : 'Your answer'} 
                  value={formData.currentEmployer} 
                  onChange={(e) => handleChoice('currentEmployer', e.target.value)}
                  disabled={formData.isCurrentlyEmployed === 'No, I am not employed.'}
                  required={formData.isCurrentlyEmployed !== 'No, I am not employed.' && formData.isCurrentlyEmployed !== ''}
                />
              </div>

              <div className={`form-group${formData.isCurrentlyEmployed === 'No, I am not employed.' ? ' is-disabled-field' : ''}`}>
                <label>If currently employed, what is your role or position title?{formData.isCurrentlyEmployed !== 'No, I am not employed.' ? ' *' : ''}</label>
                <p className="input-hint input-hint-muted">e.g. Collections Agent, Customer Service Representative, Outbound Sales Agent</p>
                <input 
                  type="text" 
                  placeholder={formData.isCurrentlyEmployed === 'No, I am not employed.' ? 'N/A' : 'Your answer'} 
                  value={formData.currentRole} 
                  onChange={(e) => handleChoice('currentRole', e.target.value)}
                  disabled={formData.isCurrentlyEmployed === 'No, I am not employed.'}
                  required={formData.isCurrentlyEmployed !== 'No, I am not employed.' && formData.isCurrentlyEmployed !== ''}
                />
              </div>

              <div className={`form-group${formData.isCurrentlyEmployed === 'No, I am not employed.' ? ' is-disabled-field' : ''}`}>
                <label>If currently employed, indicate the period of your employment.{formData.isCurrentlyEmployed !== 'No, I am not employed.' ? ' *' : ''}</label>
                <p className="input-hint input-hint-muted">For example: January 2, 2025 - up to present - or - January 2, 2025, up to March 21, 2025</p>
                <input 
                  type="text" 
                  placeholder={formData.isCurrentlyEmployed === 'No, I am not employed.' ? 'N/A' : 'Your answer'} 
                  value={formData.currentEmploymentPeriod} 
                  onChange={(e) => handleChoice('currentEmploymentPeriod', e.target.value)}
                  disabled={formData.isCurrentlyEmployed === 'No, I am not employed.'}
                  required={formData.isCurrentlyEmployed !== 'No, I am not employed.' && formData.isCurrentlyEmployed !== ''}
                />
              </div>
              </div>

              <div className="form-field-group">
                <p className="form-field-group-title">{lang === 'zh' ? '背景调查' : 'Background & Compliance'}</p>
              <div className="form-group">
                <label>How soon can you start if a Job Offer is given today? *</label>
                <select 
                  className="form-select" 
                  value={formData.noticePeriod} 
                  onChange={(e) => handleChoice('noticePeriod', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="ASAP or after 1 day notice period">ASAP or after 1 day notice period</option>
                  <option value="After 2 to 4 days notice period">After 2 to 4 days notice period</option>
                  <option value="After 1 week notice period">After 1 week notice period</option>
                  <option value="After 2 weeks notice period">After 2 weeks notice period</option>
                  <option value="After 3 week notice period.">After 3 week notice period.</option>
                  <option value="After 4 weeks notice period.">After 4 weeks notice period.</option>
                  <option value="After more than 1 month notice period">After more than 1 month notice period</option>
                </select>
              </div>

              <div className="form-group">
                <label>Outstanding or pending Job Offer from another company? *</label>
                <select 
                  className="form-select" 
                  value={formData.hasOutstandingJobOffer} 
                  onChange={(e) => handleChoice('hasOutstandingJobOffer', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              <div className="form-group">
                <label>Ready for comprehensive background check (employment & criminal)? *</label>
                <select 
                  className="form-select" 
                  value={formData.comprehensiveBackgroundCheck} 
                  onChange={(e) => handleChoice('comprehensiveBackgroundCheck', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="Not really, I have a pending civil/criminal/administrative case.">Not really, I have a pending civil/criminal/administrative case.</option>
                  <option value="Not really, I was terminated or had an administrative case with one of my previous companies.">Not really, I was terminated or had an administrative case with one of my previous companies.</option>
                </select>
              </div>

              <div className="form-group">
                <label>Drug-free workplace policy — any record of prohibited drug use? *</label>
                <p className="input-hint input-hint-muted">Mandatory drug testing applies throughout employment at HPBO.</p>
                <select 
                  className="form-select" 
                  value={formData.drugFreeWorkplace} 
                  onChange={(e) => handleChoice('drugFreeWorkplace', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="No record or history of being positive to any illegal substance.">No record or history of being positive to any illegal substance.</option>
                  <option value="Yes, there was a history of being positive to illegal substance in the past.">Yes, there was a history of being positive to illegal substance in the past.</option>
                  <option value="I had history of being rehabilitated but I am now clean. I am confident that I will not be positive to the drug testing for any illegal substance.">I had history of being rehabilitated but I am now clean. I am confident that I will not be positive to the drug testing for any illegal substance.</option>
                </select>
              </div>

              <div className="form-group">
                <label>Terminated or cited for infractions in past employment? *</label>
                <p className="input-hint input-hint-muted">Failure to declare may be considered an act of dishonesty. Inform your recruiter if applicable.</p>
                <select 
                  className="form-select" 
                  value={formData.pastInfractionsStatus} 
                  onChange={(e) => handleChoice('pastInfractionsStatus', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="No, my record has been cleared from any infractions from any of my previous employers.">No, my record has been cleared from any infractions from any of my previous employers.</option>
                  <option value="Yes, I was terminated by one of my previous Employers in the past.">Yes, I was terminated by one of my previous Employers in the past.</option>
                  <option value="Yes, I was terminated by one of my previous Employer in the past, but I filed a labor case for illegal dismissal against that Employer.">Yes, I was terminated by one of my previous Employer in the past, but I filed a labor case for illegal dismissal against that Employer.</option>
                  <option value="I was not terminated but I had been given a Notice to Explain. I will pro-actively explain this to the HR Manager on my Day 1.">I was not terminated but I had been given a Notice to Explain. I will pro-actively explain this to the HR Manager on my Day 1.</option>
                  <option value="Yes I received a Notice to Explain in the past, but I gracefully resigned because of that.">Yes I received a Notice to Explain in the past, but I gracefully resigned because of that.</option>
                </select>
              </div>
              </div>

              <p className="form-field-group-title employment-section-heading">{lang === 'zh' ? '工作历史' : 'Employment History'}</p>

              <div className="employment-block">
              <p className="employment-block-title">Employment History (1st)</p>
              <div className="form-group">
                <label>Company name, role/position, and employment period *</label>
                <p className="input-hint input-hint-muted">
                  For example:<br/>
                  <strong>Delta Prime, Inc.</strong><br/>
                  <strong>Outbound Sales Agent</strong><br/>
                  <strong>January 1, 2024 - March 1, 2025</strong><br/><br/>
                  Write <strong>"NA"</strong> if not applicable.
                </p>
                <textarea 
                  placeholder="Your answer" 
                  value={formData.employmentHistory1st} 
                  onChange={(e) => handleChoice('employmentHistory1st', e.target.value)} 
                ></textarea>
              </div>

              <div className="input-grid">
              <div className="form-group">
                <label>Reason for separation *</label>
                <select 
                  className="form-select" 
                  value={formData.reasonForSeparation1st} 
                  onChange={(e) => handleChoice('reasonForSeparation1st', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Resignation - Attendance Issues">Resignation - Attendance Issues</option>
                  <option value="Resignation - Benefits Issue">Resignation - Benefits Issue</option>
                  <option value="Resignation - Better Pay">Resignation - Better Pay</option>
                  <option value="Resignation - Career Opportunity">Resignation - Career Opportunity</option>
                  <option value="Resignation - Conflict with a co-worker">Resignation - Conflict with a co-worker</option>
                  <option value="Resignation - Desire to pursue further education">Resignation - Desire to pursue further education</option>
                  <option value="Resignation - Family Reason">Resignation - Family Reason</option>
                  <option value="Resignation - Health Reason">Resignation - Health Reason</option>
                  <option value="Resignation - Incompatibility with company values or mission">Resignation - Incompatibility with company values or mission</option>
                  <option value="Resignation - Lack of job security">Resignation - Lack of job security</option>
                  <option value="Resignation - Lack of recognition or appreciation">Resignation - Lack of recognition or appreciation</option>
                  <option value="Resignation - Legal or compliance issues of the Company">Resignation - Legal or compliance issues of the Company</option>
                  <option value="Resignation - Limited training and development opportunities">Resignation - Limited training and development opportunities</option>
                  <option value="Resignation - Management Issue">Resignation - Management Issue</option>
                  <option value="Resignation - Overwhelming workload or unrealistic expectations">Resignation - Overwhelming workload or unrealistic expectations</option>
                  <option value="Resignation - Personal business ventures or entrepreneurship">Resignation - Personal business ventures or entrepreneurship</option>
                  <option value="Resignation - Poor leadership or communication gaps">Resignation - Poor leadership or communication gaps</option>
                  <option value="Resignation - Relocation or migration (local or abroad)">Resignation - Relocation or migration (local or abroad)</option>
                  <option value="Resignation - Toxic workplace culture">Resignation - Toxic workplace culture</option>
                  <option value="Resignation - Unclear career path or promotion delays">Resignation - Unclear career path or promotion delays</option>
                  <option value="Resignation - Work Burn Out">Resignation - Work Burn Out</option>
                  <option value="Resignation - Work Location Issue">Resignation - Work Location Issue</option>
                  <option value="Resignation - Personal Reason">Resignation - Personal Reason</option>
                  <option value="Resignation - Mental Health Reason">Resignation - Mental Health Reason</option>
                  <option value="Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)">Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)</option>
                  <option value="Closure of Company Business">Closure of Company Business</option>
                  <option value="End of Project Contract">End of Project Contract</option>
                  <option value="End of Probationary Contract">End of Probationary Contract</option>
                  <option value="Early Retirement Availment">Early Retirement Availment</option>
                  <option value="Separated due to Redundancy">Separated due to Redundancy</option>
                  <option value="Separated due to Retrenchment">Separated due to Retrenchment</option>
                  <option value="Resignation because I was given a graceful exit (but I can explain what happened).">Resignation because I was given a graceful exit (but I can explain what happened).</option>
                  <option value="Terminated due to a case but I can explain this to HR.">Terminated due to a case but I can explain this to HR.</option>
                  <option value="Terminated due to AWOL but I can explain this to HR.">Terminated due to AWOL but I can explain this to HR.</option>
                  <option value="Not Applicable">Not Applicable</option>
                  <option value="Others. Inform HR if you choose 'Others'">Others. Inform HR if you choose "Others"</option>
                </select>
              </div>

              <div className="form-group">
                <label>Clearance & COE status *</label>
                <select 
                  className="form-select" 
                  value={formData.clearanceAndCoe1st} 
                  onChange={(e) => handleChoice('clearanceAndCoe1st', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.">Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.</option>
                  <option value="No, I did not process an Exit Clearance with this Company.">No, I did not process an Exit Clearance with this Company.</option>
                  <option value="I already processed my Exit Clearance but I have not received by Certificate of Employment.">I already processed my Exit Clearance but I have not received by Certificate of Employment.</option>
                  <option value="For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.">For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.</option>
                  <option value="The Company is already closed.">The Company is already closed.</option>
                  <option value="The Company is located outside the Philippines.">The Company is located outside the Philippines.</option>
                  <option value="Not Applicable.">Not Applicable.</option>
                </select>
              </div>
              </div>
              </div>

              <div className="employment-block">
              <p className="employment-block-title">Employment History (2nd)</p>
              <div className="form-group">
                <label>Company name, role/position, and employment period *</label>
                <p className="input-hint input-hint-muted">
                  For example:<br/>
                  <strong>Delta Prime, Inc.</strong><br/>
                  <strong>Outbound Sales Agent</strong><br/>
                  <strong>January 1, 2024 - March 1, 2025</strong><br/><br/>
                  Write <strong>"NA"</strong> if not applicable.
                </p>
                <textarea 
                  placeholder="Your answer" 
                  value={formData.employmentHistory2nd} 
                  onChange={(e) => handleChoice('employmentHistory2nd', e.target.value)} 
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Reason for separation *</label>
                <select 
                  className="form-select" 
                  value={formData.reasonForSeparation2nd} 
                  onChange={(e) => handleChoice('reasonForSeparation2nd', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Resignation - Attendance Issues">Resignation - Attendance Issues</option>
                  <option value="Resignation - Benefits Issue">Resignation - Benefits Issue</option>
                  <option value="Resignation - Better Pay">Resignation - Better Pay</option>
                  <option value="Resignation - Career Opportunity">Resignation - Career Opportunity</option>
                  <option value="Resignation - Conflict with a co-worker">Resignation - Conflict with a co-worker</option>
                  <option value="Resignation - Desire to pursue further education">Resignation - Desire to pursue further education</option>
                  <option value="Resignation - Family Reason">Resignation - Family Reason</option>
                  <option value="Resignation - Health Reason">Resignation - Health Reason</option>
                  <option value="Resignation - Incompatibility with company values or mission">Resignation - Incompatibility with company values or mission</option>
                  <option value="Resignation - Lack of job security">Resignation - Lack of job security</option>
                  <option value="Resignation - Lack of recognition or appreciation">Resignation - Lack of recognition or appreciation</option>
                  <option value="Resignation - Legal or compliance issues of the Company">Resignation - Legal or compliance issues of the Company</option>
                  <option value="Resignation - Limited training and development opportunities">Resignation - Limited training and development opportunities</option>
                  <option value="Resignation - Management Issue">Resignation - Management Issue</option>
                  <option value="Resignation - Overwhelming workload or unrealistic expectations">Resignation - Overwhelming workload or unrealistic expectations</option>
                  <option value="Resignation - Personal business ventures or entrepreneurship">Resignation - Personal business ventures or entrepreneurship</option>
                  <option value="Resignation - Poor leadership or communication gaps">Resignation - Poor leadership or communication gaps</option>
                  <option value="Resignation - Relocation or migration (local or abroad)">Resignation - Relocation or migration (local or abroad)</option>
                  <option value="Resignation - Toxic workplace culture">Resignation - Toxic workplace culture</option>
                  <option value="Resignation - Unclear career path or promotion delays">Resignation - Unclear career path or promotion delays</option>
                  <option value="Resignation - Work Burn Out">Resignation - Work Burn Out</option>
                  <option value="Resignation - Work Location Issue">Resignation - Work Location Issue</option>
                  <option value="Resignation - Personal Reason">Resignation - Personal Reason</option>
                  <option value="Resignation - Mental Health Reason">Resignation - Mental Health Reason</option>
                  <option value="Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)">Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)</option>
                  <option value="Closure of Company Business">Closure of Company Business</option>
                  <option value="End of Project Contract">End of Project Contract</option>
                  <option value="End of Probationary Contract">End of Probationary Contract</option>
                  <option value="Early Retirement Availment">Early Retirement Availment</option>
                  <option value="Separated due to Redundancy">Separated due to Redundancy</option>
                  <option value="Separated due to Retrenchment">Separated due to Retrenchment</option>
                  <option value="Resignation because I was given a graceful exit (but I can explain what happened).">Resignation because I was given a graceful exit (but I can explain what happened).</option>
                  <option value="Terminated due to a case but I can explain this to HR.">Terminated due to a case but I can explain this to HR.</option>
                  <option value="Terminated due to AWOL but I can explain this to HR.">Terminated due to AWOL but I can explain this to HR.</option>
                  <option value="Not Applicable">Not Applicable</option>
                  <option value="Others. Inform HR if you choose 'Others'">Others. Inform HR if you choose "Others"</option>
                </select>
              </div>

              <div className="form-group">
                <label>Clearance & COE status *</label>
                <select 
                  className="form-select" 
                  value={formData.clearanceAndCoe2nd} 
                  onChange={(e) => handleChoice('clearanceAndCoe2nd', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.">Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.</option>
                  <option value="No, I did not process an Exit Clearance with this Company.">No, I did not process an Exit Clearance with this Company.</option>
                  <option value="I already processed my Exit Clearance but I have not received by Certificate of Employment.">I already processed my Exit Clearance but I have not received by Certificate of Employment.</option>
                  <option value="For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.">For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.</option>
                  <option value="The Company is already closed.">The Company is already closed.</option>
                  <option value="The Company is located outside the Philippines.">The Company is located outside the Philippines.</option>
                  <option value="Not Applicable.">Not Applicable.</option>
                </select>
              </div>
              </div>

              <div className="employment-block">
              <p className="employment-block-title">Employment History (3rd)</p>
              <div className="form-group">
                <label>Company name, role/position, and employment period *</label>
                <p className="input-hint input-hint-muted">
                  For example:<br/>
                  <strong>Delta Prime, Inc.</strong><br/>
                  <strong>Outbound Sales Agent</strong><br/>
                  <strong>January 1, 2024 - March 1, 2025</strong><br/><br/>
                  Write <strong>"NA"</strong> if not applicable.
                </p>
                <textarea 
                  placeholder="Your answer" 
                  value={formData.employmentHistory3rd} 
                  onChange={(e) => handleChoice('employmentHistory3rd', e.target.value)} 
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Reason for separation *</label>
                <select 
                  className="form-select" 
                  value={formData.reasonForSeparation3rd} 
                  onChange={(e) => handleChoice('reasonForSeparation3rd', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Resignation - Attendance Issues">Resignation - Attendance Issues</option>
                  <option value="Resignation - Benefits Issue">Resignation - Benefits Issue</option>
                  <option value="Resignation - Better Pay">Resignation - Better Pay</option>
                  <option value="Resignation - Career Opportunity">Resignation - Career Opportunity</option>
                  <option value="Resignation - Conflict with a co-worker">Resignation - Conflict with a co-worker</option>
                  <option value="Resignation - Desire to pursue further education">Resignation - Desire to pursue further education</option>
                  <option value="Resignation - Family Reason">Resignation - Family Reason</option>
                  <option value="Resignation - Health Reason">Resignation - Health Reason</option>
                  <option value="Resignation - Incompatibility with company values or mission">Resignation - Incompatibility with company values or mission</option>
                  <option value="Resignation - Lack of job security">Resignation - Lack of job security</option>
                  <option value="Resignation - Lack of recognition or appreciation">Resignation - Lack of recognition or appreciation</option>
                  <option value="Resignation - Legal or compliance issues of the Company">Resignation - Legal or compliance issues of the Company</option>
                  <option value="Resignation - Limited training and development opportunities">Resignation - Limited training and development opportunities</option>
                  <option value="Resignation - Management Issue">Resignation - Management Issue</option>
                  <option value="Resignation - Overwhelming workload or unrealistic expectations">Resignation - Overwhelming workload or unrealistic expectations</option>
                  <option value="Resignation - Personal business ventures or entrepreneurship">Resignation - Personal business ventures or entrepreneurship</option>
                  <option value="Resignation - Poor leadership or communication gaps">Resignation - Poor leadership or communication gaps</option>
                  <option value="Resignation - Relocation or migration (local or abroad)">Resignation - Relocation or migration (local or abroad)</option>
                  <option value="Resignation - Toxic workplace culture">Resignation - Toxic workplace culture</option>
                  <option value="Resignation - Unclear career path or promotion delays">Resignation - Unclear career path or promotion delays</option>
                  <option value="Resignation - Work Burn Out">Resignation - Work Burn Out</option>
                  <option value="Resignation - Work Location Issue">Resignation - Work Location Issue</option>
                  <option value="Resignation - Personal Reason">Resignation - Personal Reason</option>
                  <option value="Resignation - Mental Health Reason">Resignation - Mental Health Reason</option>
                  <option value="Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)">Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)</option>
                  <option value="Closure of Company Business">Closure of Company Business</option>
                  <option value="End of Project Contract">End of Project Contract</option>
                  <option value="End of Probationary Contract">End of Probationary Contract</option>
                  <option value="Early Retirement Availment">Early Retirement Availment</option>
                  <option value="Separated due to Redundancy">Separated due to Redundancy</option>
                  <option value="Separated due to Retrenchment">Separated due to Retrenchment</option>
                  <option value="Resignation because I was given a graceful exit (but I can explain what happened).">Resignation because I was given a graceful exit (but I can explain what happened).</option>
                  <option value="Terminated due to a case but I can explain this to HR.">Terminated due to a case but I can explain this to HR.</option>
                  <option value="Terminated due to AWOL but I can explain this to HR.">Terminated due to AWOL but I can explain this to HR.</option>
                  <option value="Not Applicable">Not Applicable</option>
                  <option value="Others. Inform HR if you choose 'Others'">Others. Inform HR if you choose "Others"</option>
                </select>
              </div>

              <div className="form-group">
                <label>Clearance & COE status *</label>
                <select 
                  className="form-select" 
                  value={formData.clearanceAndCoe3rd} 
                  onChange={(e) => handleChoice('clearanceAndCoe3rd', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.">Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.</option>
                  <option value="No, I did not process an Exit Clearance with this Company.">No, I did not process an Exit Clearance with this Company.</option>
                  <option value="I already processed my Exit Clearance but I have not received by Certificate of Employment.">I already processed my Exit Clearance but I have not received by Certificate of Employment.</option>
                  <option value="For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.">For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.</option>
                  <option value="The Company is already closed.">The Company is already closed.</option>
                  <option value="The Company is located outside the Philippines.">The Company is located outside the Philippines.</option>
                  <option value="Not Applicable.">Not Applicable.</option>
                </select>
              </div>

              </div>

              <div className="employment-block">
              <p className="employment-block-title">Employment History (4th)</p>
              <div className="form-group">
                <label>Company name, role/position, and employment period *</label>
                <p className="input-hint input-hint-muted">
                  For example:<br/>
                  <strong>Delta Prime, Inc.</strong><br/>
                  <strong>Outbound Sales Agent</strong><br/>
                  <strong>January 1, 2024 - March 1, 2025</strong><br/><br/>
                  Write <strong>"NA"</strong> if not applicable.
                </p>
                <textarea 
                  placeholder="Your answer" 
                  value={formData.employmentHistory4th} 
                  onChange={(e) => handleChoice('employmentHistory4th', e.target.value)} 
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Reason for separation *</label>
                <select 
                  className="form-select" 
                  value={formData.reasonForSeparation4th} 
                  onChange={(e) => handleChoice('reasonForSeparation4th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Resignation - Attendance Issues">Resignation - Attendance Issues</option>
                  <option value="Resignation - Benefits Issue">Resignation - Benefits Issue</option>
                  <option value="Resignation - Better Pay">Resignation - Better Pay</option>
                  <option value="Resignation - Career Opportunity">Resignation - Career Opportunity</option>
                  <option value="Resignation - Conflict with a co-worker">Resignation - Conflict with a co-worker</option>
                  <option value="Resignation - Desire to pursue further education">Resignation - Desire to pursue further education</option>
                  <option value="Resignation - Family Reason">Resignation - Family Reason</option>
                  <option value="Resignation - Health Reason">Resignation - Health Reason</option>
                  <option value="Resignation - Incompatibility with company values or mission">Resignation - Incompatibility with company values or mission</option>
                  <option value="Resignation - Lack of job security">Resignation - Lack of job security</option>
                  <option value="Resignation - Lack of recognition or appreciation">Resignation - Lack of recognition or appreciation</option>
                  <option value="Resignation - Legal or compliance issues of the Company">Resignation - Legal or compliance issues of the Company</option>
                  <option value="Resignation - Limited training and development opportunities">Resignation - Limited training and development opportunities</option>
                  <option value="Resignation - Management Issue">Resignation - Management Issue</option>
                  <option value="Resignation - Overwhelming workload or unrealistic expectations">Resignation - Overwhelming workload or unrealistic expectations</option>
                  <option value="Resignation - Personal business ventures or entrepreneurship">Resignation - Personal business ventures or entrepreneurship</option>
                  <option value="Resignation - Poor leadership or communication gaps">Resignation - Poor leadership or communication gaps</option>
                  <option value="Resignation - Relocation or migration (local or abroad)">Resignation - Relocation or migration (local or abroad)</option>
                  <option value="Resignation - Toxic workplace culture">Resignation - Toxic workplace culture</option>
                  <option value="Resignation - Unclear career path or promotion delays">Resignation - Unclear career path or promotion delays</option>
                  <option value="Resignation - Work Burn Out">Resignation - Work Burn Out</option>
                  <option value="Resignation - Work Location Issue">Resignation - Work Location Issue</option>
                  <option value="Resignation - Personal Reason">Resignation - Personal Reason</option>
                  <option value="Resignation - Mental Health Reason">Resignation - Mental Health Reason</option>
                  <option value="Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)">Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)</option>
                  <option value="Closure of Company Business">Closure of Company Business</option>
                  <option value="End of Project Contract">End of Project Contract</option>
                  <option value="End of Probationary Contract">End of Probationary Contract</option>
                  <option value="Early Retirement Availment">Early Retirement Availment</option>
                  <option value="Separated due to Redundancy">Separated due to Redundancy</option>
                  <option value="Separated due to Retrenchment">Separated due to Retrenchment</option>
                  <option value="Resignation because I was given a graceful exit (but I can explain what happened).">Resignation because I was given a graceful exit (but I can explain what happened).</option>
                  <option value="Terminated due to a case but I can explain this to HR.">Terminated due to a case but I can explain this to HR.</option>
                  <option value="Terminated due to AWOL but I can explain this to HR.">Terminated due to AWOL but I can explain this to HR.</option>
                  <option value="Not Applicable">Not Applicable</option>
                  <option value="Others. Inform HR if you choose 'Others'">Others. Inform HR if you choose "Others"</option>
                </select>
              </div>

              <div className="form-group">
                <label>Clearance & COE status *</label>
                <select 
                  className="form-select" 
                  value={formData.clearanceAndCoe4th} 
                  onChange={(e) => handleChoice('clearanceAndCoe4th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.">Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.</option>
                  <option value="No, I did not process an Exit Clearance with this Company.">No, I did not process an Exit Clearance with this Company.</option>
                  <option value="I already processed my Exit Clearance but I have not received by Certificate of Employment.">I already processed my Exit Clearance but I have not received by Certificate of Employment.</option>
                  <option value="For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.">For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.</option>
                  <option value="The Company is already closed.">The Company is already closed.</option>
                  <option value="The Company is located outside the Philippines.">The Company is located outside the Philippines.</option>
                  <option value="Not Applicable.">Not Applicable.</option>
                </select>
              </div>

              </div>

              <div className="employment-block">
              <p className="employment-block-title">Employment History (5th)</p>
              <div className="form-group">
                <label>Company name, role/position, and employment period *</label>
                <p className="input-hint input-hint-muted">
                  For example:<br/>
                  <strong>Delta Prime, Inc.</strong><br/>
                  <strong>Outbound Sales Agent</strong><br/>
                  <strong>January 1, 2024 - March 1, 2025</strong><br/><br/>
                  Write <strong>"NA"</strong> if not applicable.
                </p>
                <textarea 
                  placeholder="Your answer" 
                  value={formData.employmentHistory5th} 
                  onChange={(e) => handleChoice('employmentHistory5th', e.target.value)} 
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Reason for separation *</label>
                <select 
                  className="form-select" 
                  value={formData.reasonForSeparation5th} 
                  onChange={(e) => handleChoice('reasonForSeparation5th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Resignation - Attendance Issues">Resignation - Attendance Issues</option>
                  <option value="Resignation - Benefits Issue">Resignation - Benefits Issue</option>
                  <option value="Resignation - Better Pay">Resignation - Better Pay</option>
                  <option value="Resignation - Career Opportunity">Resignation - Career Opportunity</option>
                  <option value="Resignation - Conflict with a co-worker">Resignation - Conflict with a co-worker</option>
                  <option value="Resignation - Desire to pursue further education">Resignation - Desire to pursue further education</option>
                  <option value="Resignation - Family Reason">Resignation - Family Reason</option>
                  <option value="Resignation - Health Reason">Resignation - Health Reason</option>
                  <option value="Resignation - Incompatibility with company values or mission">Resignation - Incompatibility with company values or mission</option>
                  <option value="Resignation - Lack of job security">Resignation - Lack of job security</option>
                  <option value="Resignation - Lack of recognition or appreciation">Resignation - Lack of recognition or appreciation</option>
                  <option value="Resignation - Legal or compliance issues of the Company">Resignation - Legal or compliance issues of the Company</option>
                  <option value="Resignation - Limited training and development opportunities">Resignation - Limited training and development opportunities</option>
                  <option value="Resignation - Management Issue">Resignation - Management Issue</option>
                  <option value="Resignation - Overwhelming workload or unrealistic expectations">Resignation - Overwhelming workload or unrealistic expectations</option>
                  <option value="Resignation - Personal business ventures or entrepreneurship">Resignation - Personal business ventures or entrepreneurship</option>
                  <option value="Resignation - Poor leadership or communication gaps">Resignation - Poor leadership or communication gaps</option>
                  <option value="Resignation - Relocation or migration (local or abroad)">Resignation - Relocation or migration (local or abroad)</option>
                  <option value="Resignation - Toxic workplace culture">Resignation - Toxic workplace culture</option>
                  <option value="Resignation - Unclear career path or promotion delays">Resignation - Unclear career path or promotion delays</option>
                  <option value="Resignation - Work Burn Out">Resignation - Work Burn Out</option>
                  <option value="Resignation - Work Location Issue">Resignation - Work Location Issue</option>
                  <option value="Resignation - Personal Reason">Resignation - Personal Reason</option>
                  <option value="Resignation - Mental Health Reason">Resignation - Mental Health Reason</option>
                  <option value="Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)">Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)</option>
                  <option value="Closure of Company Business">Closure of Company Business</option>
                  <option value="End of Project Contract">End of Project Contract</option>
                  <option value="End of Probationary Contract">End of Probationary Contract</option>
                  <option value="Early Retirement Availment">Early Retirement Availment</option>
                  <option value="Separated due to Redundancy">Separated due to Redundancy</option>
                  <option value="Separated due to Retrenchment">Separated due to Retrenchment</option>
                  <option value="Resignation because I was given a graceful exit (but I can explain what happened).">Resignation because I was given a graceful exit (but I can explain what happened).</option>
                  <option value="Terminated due to a case but I can explain this to HR.">Terminated due to a case but I can explain this to HR.</option>
                  <option value="Terminated due to AWOL but I can explain this to HR.">Terminated due to AWOL but I can explain this to HR.</option>
                  <option value="Not Applicable">Not Applicable</option>
                  <option value="Others. Inform HR if you choose 'Others'">Others. Inform HR if you choose "Others"</option>
                </select>
              </div>

              <div className="form-group">
                <label>Clearance & COE status *</label>
                <select 
                  className="form-select" 
                  value={formData.clearanceAndCoe5th} 
                  onChange={(e) => handleChoice('clearanceAndCoe5th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.">Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.</option>
                  <option value="No, I did not process an Exit Clearance with this Company.">No, I did not process an Exit Clearance with this Company.</option>
                  <option value="I already processed my Exit Clearance but I have not received by Certificate of Employment.">I already processed my Exit Clearance but I have not received by Certificate of Employment.</option>
                  <option value="For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.">For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.</option>
                  <option value="The Company is already closed.">The Company is already closed.</option>
                  <option value="The Company is located outside the Philippines.">The Company is located outside the Philippines.</option>
                  <option value="Not Applicable.">Not Applicable.</option>
                </select>
              </div>

              </div>

              <div className="employment-block">
              <p className="employment-block-title">Employment History (6th)</p>
              <div className="form-group">
                <label>Company name, role/position, and employment period *</label>
                <p className="input-hint input-hint-muted">
                  For example:<br/>
                  <strong>Delta Prime, Inc.</strong><br/>
                  <strong>Outbound Sales Agent</strong><br/>
                  <strong>January 1, 2024 - March 1, 2025</strong><br/><br/>
                  Write <strong>"NA"</strong> if not applicable.
                </p>
                <textarea 
                  placeholder="Your answer" 
                  value={formData.employmentHistory6th} 
                  onChange={(e) => handleChoice('employmentHistory6th', e.target.value)} 
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Reason for separation *</label>
                <select 
                  className="form-select" 
                  value={formData.reasonForSeparation6th} 
                  onChange={(e) => handleChoice('reasonForSeparation6th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Resignation - Attendance Issues">Resignation - Attendance Issues</option>
                  <option value="Resignation - Benefits Issue">Resignation - Benefits Issue</option>
                  <option value="Resignation - Better Pay">Resignation - Better Pay</option>
                  <option value="Resignation - Career Opportunity">Resignation - Career Opportunity</option>
                  <option value="Resignation - Conflict with a co-worker">Resignation - Conflict with a co-worker</option>
                  <option value="Resignation - Desire to pursue further education">Resignation - Desire to pursue further education</option>
                  <option value="Resignation - Family Reason">Resignation - Family Reason</option>
                  <option value="Resignation - Health Reason">Resignation - Health Reason</option>
                  <option value="Resignation - Incompatibility with company values or mission">Resignation - Incompatibility with company values or mission</option>
                  <option value="Resignation - Lack of job security">Resignation - Lack of job security</option>
                  <option value="Resignation - Lack of recognition or appreciation">Resignation - Lack of recognition or appreciation</option>
                  <option value="Resignation - Legal or compliance issues of the Company">Resignation - Legal or compliance issues of the Company</option>
                  <option value="Resignation - Limited training and development opportunities">Resignation - Limited training and development opportunities</option>
                  <option value="Resignation - Management Issue">Resignation - Management Issue</option>
                  <option value="Resignation - Overwhelming workload or unrealistic expectations">Resignation - Overwhelming workload or unrealistic expectations</option>
                  <option value="Resignation - Personal business ventures or entrepreneurship">Resignation - Personal business ventures or entrepreneurship</option>
                  <option value="Resignation - Poor leadership or communication gaps">Resignation - Poor leadership or communication gaps</option>
                  <option value="Resignation - Relocation or migration (local or abroad)">Resignation - Relocation or migration (local or abroad)</option>
                  <option value="Resignation - Toxic workplace culture">Resignation - Toxic workplace culture</option>
                  <option value="Resignation - Unclear career path or promotion delays">Resignation - Unclear career path or promotion delays</option>
                  <option value="Resignation - Work Burn Out">Resignation - Work Burn Out</option>
                  <option value="Resignation - Work Location Issue">Resignation - Work Location Issue</option>
                  <option value="Resignation - Personal Reason">Resignation - Personal Reason</option>
                  <option value="Resignation - Mental Health Reason">Resignation - Mental Health Reason</option>
                  <option value="Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)">Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)</option>
                  <option value="Closure of Company Business">Closure of Company Business</option>
                  <option value="End of Project Contract">End of Project Contract</option>
                  <option value="End of Probationary Contract">End of Probationary Contract</option>
                  <option value="Early Retirement Availment">Early Retirement Availment</option>
                  <option value="Separated due to Redundancy">Separated due to Redundancy</option>
                  <option value="Separated due to Retrenchment">Separated due to Retrenchment</option>
                  <option value="Resignation because I was given a graceful exit (but I can explain what happened).">Resignation because I was given a graceful exit (but I can explain what happened).</option>
                  <option value="Terminated due to a case but I can explain this to HR.">Terminated due to a case but I can explain this to HR.</option>
                  <option value="Terminated due to AWOL but I can explain this to HR.">Terminated due to AWOL but I can explain this to HR.</option>
                  <option value="Not Applicable">Not Applicable</option>
                  <option value="Others. Inform HR if you choose 'Others'">Others. Inform HR if you choose "Others"</option>
                </select>
              </div>

              <div className="form-group">
                <label>Clearance & COE status *</label>
                <select 
                  className="form-select" 
                  value={formData.clearanceAndCoe6th} 
                  onChange={(e) => handleChoice('clearanceAndCoe6th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.">Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.</option>
                  <option value="No, I did not process an Exit Clearance with this Company.">No, I did not process an Exit Clearance with this Company.</option>
                  <option value="I already processed my Exit Clearance but I have not received by Certificate of Employment.">I already processed my Exit Clearance but I have not received by Certificate of Employment.</option>
                  <option value="For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.">For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.</option>
                  <option value="The Company is already closed.">The Company is already closed.</option>
                  <option value="The Company is located outside the Philippines.">The Company is located outside the Philippines.</option>
                  <option value="Not Applicable.">Not Applicable.</option>
                </select>
              </div>

              </div>

              <div className="employment-block">
              <p className="employment-block-title">Employment History (7th)</p>
              <div className="form-group">
                <label>Company name, role/position, and employment period *</label>
                <p className="input-hint input-hint-muted">
                  For example:<br/>
                  <strong>Delta Prime, Inc.</strong><br/>
                  <strong>Outbound Sales Agent</strong><br/>
                  <strong>January 1, 2024 - March 1, 2025</strong><br/><br/>
                  Write <strong>"NA"</strong> if not applicable.
                </p>
                <textarea 
                  placeholder="Your answer" 
                  value={formData.employmentHistory7th} 
                  onChange={(e) => handleChoice('employmentHistory7th', e.target.value)} 
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Reason for separation *</label>
                <select 
                  className="form-select" 
                  value={formData.reasonForSeparation7th} 
                  onChange={(e) => handleChoice('reasonForSeparation7th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Resignation - Attendance Issues">Resignation - Attendance Issues</option>
                  <option value="Resignation - Benefits Issue">Resignation - Benefits Issue</option>
                  <option value="Resignation - Better Pay">Resignation - Better Pay</option>
                  <option value="Resignation - Career Opportunity">Resignation - Career Opportunity</option>
                  <option value="Resignation - Conflict with a co-worker">Resignation - Conflict with a co-worker</option>
                  <option value="Resignation - Desire to pursue further education">Resignation - Desire to pursue further education</option>
                  <option value="Resignation - Family Reason">Resignation - Family Reason</option>
                  <option value="Resignation - Health Reason">Resignation - Health Reason</option>
                  <option value="Resignation - Incompatibility with company values or mission">Resignation - Incompatibility with company values or mission</option>
                  <option value="Resignation - Lack of job security">Resignation - Lack of job security</option>
                  <option value="Resignation - Lack of recognition or appreciation">Resignation - Lack of recognition or appreciation</option>
                  <option value="Resignation - Legal or compliance issues of the Company">Resignation - Legal or compliance issues of the Company</option>
                  <option value="Resignation - Limited training and development opportunities">Resignation - Limited training and development opportunities</option>
                  <option value="Resignation - Management Issue">Resignation - Management Issue</option>
                  <option value="Resignation - Overwhelming workload or unrealistic expectations">Resignation - Overwhelming workload or unrealistic expectations</option>
                  <option value="Resignation - Personal business ventures or entrepreneurship">Resignation - Personal business ventures or entrepreneurship</option>
                  <option value="Resignation - Poor leadership or communication gaps">Resignation - Poor leadership or communication gaps</option>
                  <option value="Resignation - Relocation or migration (local or abroad)">Resignation - Relocation or migration (local or abroad)</option>
                  <option value="Resignation - Toxic workplace culture">Resignation - Toxic workplace culture</option>
                  <option value="Resignation - Unclear career path or promotion delays">Resignation - Unclear career path or promotion delays</option>
                  <option value="Resignation - Work Burn Out">Resignation - Work Burn Out</option>
                  <option value="Resignation - Work Location Issue">Resignation - Work Location Issue</option>
                  <option value="Resignation - Personal Reason">Resignation - Personal Reason</option>
                  <option value="Resignation - Mental Health Reason">Resignation - Mental Health Reason</option>
                  <option value="Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)">Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)</option>
                  <option value="Closure of Company Business">Closure of Company Business</option>
                  <option value="End of Project Contract">End of Project Contract</option>
                  <option value="End of Probationary Contract">End of Probationary Contract</option>
                  <option value="Early Retirement Availment">Early Retirement Availment</option>
                  <option value="Separated due to Redundancy">Separated due to Redundancy</option>
                  <option value="Separated due to Retrenchment">Separated due to Retrenchment</option>
                  <option value="Resignation because I was given a graceful exit (but I can explain what happened).">Resignation because I was given a graceful exit (but I can explain what happened).</option>
                  <option value="Terminated due to a case but I can explain this to HR.">Terminated due to a case but I can explain this to HR.</option>
                  <option value="Terminated due to AWOL but I can explain this to HR.">Terminated due to AWOL but I can explain this to HR.</option>
                  <option value="Not Applicable">Not Applicable</option>
                  <option value="Others. Inform HR if you choose 'Others'">Others. Inform HR if you choose "Others"</option>
                </select>
              </div>

              <div className="form-group">
                <label>Clearance & COE status *</label>
                <select 
                  className="form-select" 
                  value={formData.clearanceAndCoe7th} 
                  onChange={(e) => handleChoice('clearanceAndCoe7th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.">Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.</option>
                  <option value="No, I did not process an Exit Clearance with this Company.">No, I did not process an Exit Clearance with this Company.</option>
                  <option value="I already processed my Exit Clearance but I have not received by Certificate of Employment.">I already processed my Exit Clearance but I have not received by Certificate of Employment.</option>
                  <option value="For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.">For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.</option>
                  <option value="The Company is already closed.">The Company is already closed.</option>
                  <option value="The Company is located outside the Philippines.">The Company is located outside the Philippines.</option>
                  <option value="Not Applicable.">Not Applicable.</option>
                </select>
              </div>

              </div>

              <div className="employment-block">
              <p className="employment-block-title">Employment History (8th)</p>
              <div className="form-group">
                <label>Company name, role/position, and employment period *</label>
                <p className="input-hint input-hint-muted">
                  For example:<br/>
                  <strong>Delta Prime, Inc.</strong><br/>
                  <strong>Outbound Sales Agent</strong><br/>
                  <strong>January 1, 2024 - March 1, 2025</strong><br/><br/>
                  Write <strong>"NA"</strong> if not applicable.
                </p>
                <textarea 
                  placeholder="Your answer" 
                  value={formData.employmentHistory8th} 
                  onChange={(e) => handleChoice('employmentHistory8th', e.target.value)} 
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Reason for separation *</label>
                <select 
                  className="form-select" 
                  value={formData.reasonForSeparation8th} 
                  onChange={(e) => handleChoice('reasonForSeparation8th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Resignation - Attendance Issues">Resignation - Attendance Issues</option>
                  <option value="Resignation - Benefits Issue">Resignation - Benefits Issue</option>
                  <option value="Resignation - Better Pay">Resignation - Better Pay</option>
                  <option value="Resignation - Career Opportunity">Resignation - Career Opportunity</option>
                  <option value="Resignation - Conflict with a co-worker">Resignation - Conflict with a co-worker</option>
                  <option value="Resignation - Desire to pursue further education">Resignation - Desire to pursue further education</option>
                  <option value="Resignation - Family Reason">Resignation - Family Reason</option>
                  <option value="Resignation - Health Reason">Resignation - Health Reason</option>
                  <option value="Resignation - Incompatibility with company values or mission">Resignation - Incompatibility with company values or mission</option>
                  <option value="Resignation - Lack of job security">Resignation - Lack of job security</option>
                  <option value="Resignation - Lack of recognition or appreciation">Resignation - Lack of recognition or appreciation</option>
                  <option value="Resignation - Legal or compliance issues of the Company">Resignation - Legal or compliance issues of the Company</option>
                  <option value="Resignation - Limited training and development opportunities">Resignation - Limited training and development opportunities</option>
                  <option value="Resignation - Management Issue">Resignation - Management Issue</option>
                  <option value="Resignation - Overwhelming workload or unrealistic expectations">Resignation - Overwhelming workload or unrealistic expectations</option>
                  <option value="Resignation - Personal business ventures or entrepreneurship">Resignation - Personal business ventures or entrepreneurship</option>
                  <option value="Resignation - Poor leadership or communication gaps">Resignation - Poor leadership or communication gaps</option>
                  <option value="Resignation - Relocation or migration (local or abroad)">Resignation - Relocation or migration (local or abroad)</option>
                  <option value="Resignation - Toxic workplace culture">Resignation - Toxic workplace culture</option>
                  <option value="Resignation - Unclear career path or promotion delays">Resignation - Unclear career path or promotion delays</option>
                  <option value="Resignation - Work Burn Out">Resignation - Work Burn Out</option>
                  <option value="Resignation - Work Location Issue">Resignation - Work Location Issue</option>
                  <option value="Resignation - Personal Reason">Resignation - Personal Reason</option>
                  <option value="Resignation - Mental Health Reason">Resignation - Mental Health Reason</option>
                  <option value="Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)">Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)</option>
                  <option value="Closure of Company Business">Closure of Company Business</option>
                  <option value="End of Project Contract">End of Project Contract</option>
                  <option value="End of Probationary Contract">End of Probationary Contract</option>
                  <option value="Early Retirement Availment">Early Retirement Availment</option>
                  <option value="Separated due to Redundancy">Separated due to Redundancy</option>
                  <option value="Separated due to Retrenchment">Separated due to Retrenchment</option>
                  <option value="Resignation because I was given a graceful exit (but I can explain what happened).">Resignation because I was given a graceful exit (but I can explain what happened).</option>
                  <option value="Terminated due to a case but I can explain this to HR.">Terminated due to a case but I can explain this to HR.</option>
                  <option value="Terminated due to AWOL but I can explain this to HR.">Terminated due to AWOL but I can explain this to HR.</option>
                  <option value="Not Applicable">Not Applicable</option>
                  <option value="Others. Inform HR if you choose 'Others'">Others. Inform HR if you choose "Others"</option>
                </select>
              </div>

              <div className="form-group">
                <label>Clearance & COE status *</label>
                <select 
                  className="form-select" 
                  value={formData.clearanceAndCoe8th} 
                  onChange={(e) => handleChoice('clearanceAndCoe8th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.">Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.</option>
                  <option value="No, I did not process an Exit Clearance with this Company.">No, I did not process an Exit Clearance with this Company.</option>
                  <option value="I already processed my Exit Clearance but I have not received by Certificate of Employment.">I already processed my Exit Clearance but I have not received by Certificate of Employment.</option>
                  <option value="For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.">For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.</option>
                  <option value="The Company is already closed.">The Company is already closed.</option>
                  <option value="The Company is located outside the Philippines.">The Company is located outside the Philippines.</option>
                  <option value="Not Applicable.">Not Applicable.</option>
                </select>
              </div>

              </div>

              <div className="employment-block">
              <p className="employment-block-title">Employment History (9th)</p>
              <div className="form-group">
                <label>Company name, role/position, and employment period *</label>
                <p className="input-hint input-hint-muted">
                  For example:<br/>
                  <strong>Delta Prime, Inc.</strong><br/>
                  <strong>Outbound Sales Agent</strong><br/>
                  <strong>January 1, 2024 - March 1, 2025</strong><br/><br/>
                  Write <strong>"NA"</strong> if not applicable.
                </p>
                <textarea 
                  placeholder="Your answer" 
                  value={formData.employmentHistory9th} 
                  onChange={(e) => handleChoice('employmentHistory9th', e.target.value)} 
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Reason for separation *</label>
                <select 
                  className="form-select" 
                  value={formData.reasonForSeparation9th} 
                  onChange={(e) => handleChoice('reasonForSeparation9th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Resignation - Attendance Issues">Resignation - Attendance Issues</option>
                  <option value="Resignation - Benefits Issue">Resignation - Benefits Issue</option>
                  <option value="Resignation - Better Pay">Resignation - Better Pay</option>
                  <option value="Resignation - Career Opportunity">Resignation - Career Opportunity</option>
                  <option value="Resignation - Conflict with a co-worker">Resignation - Conflict with a co-worker</option>
                  <option value="Resignation - Desire to pursue further education">Resignation - Desire to pursue further education</option>
                  <option value="Resignation - Family Reason">Resignation - Family Reason</option>
                  <option value="Resignation - Health Reason">Resignation - Health Reason</option>
                  <option value="Resignation - Incompatibility with company values or mission">Resignation - Incompatibility with company values or mission</option>
                  <option value="Resignation - Lack of job security">Resignation - Lack of job security</option>
                  <option value="Resignation - Lack of recognition or appreciation">Resignation - Lack of recognition or appreciation</option>
                  <option value="Resignation - Legal or compliance issues of the Company">Resignation - Legal or compliance issues of the Company</option>
                  <option value="Resignation - Limited training and development opportunities">Resignation - Limited training and development opportunities</option>
                  <option value="Resignation - Management Issue">Resignation - Management Issue</option>
                  <option value="Resignation - Overwhelming workload or unrealistic expectations">Resignation - Overwhelming workload or unrealistic expectations</option>
                  <option value="Resignation - Personal business ventures or entrepreneurship">Resignation - Personal business ventures or entrepreneurship</option>
                  <option value="Resignation - Poor leadership or communication gaps">Resignation - Poor leadership or communication gaps</option>
                  <option value="Resignation - Relocation or migration (local or abroad)">Resignation - Relocation or migration (local or abroad)</option>
                  <option value="Resignation - Toxic workplace culture">Resignation - Toxic workplace culture</option>
                  <option value="Resignation - Unclear career path or promotion delays">Resignation - Unclear career path or promotion delays</option>
                  <option value="Resignation - Work Burn Out">Resignation - Work Burn Out</option>
                  <option value="Resignation - Work Location Issue">Resignation - Work Location Issue</option>
                  <option value="Resignation - Personal Reason">Resignation - Personal Reason</option>
                  <option value="Resignation - Mental Health Reason">Resignation - Mental Health Reason</option>
                  <option value="Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)">Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)</option>
                  <option value="Closure of Company Business">Closure of Company Business</option>
                  <option value="End of Project Contract">End of Project Contract</option>
                  <option value="End of Probationary Contract">End of Probationary Contract</option>
                  <option value="Early Retirement Availment">Early Retirement Availment</option>
                  <option value="Separated due to Redundancy">Separated due to Redundancy</option>
                  <option value="Separated due to Retrenchment">Separated due to Retrenchment</option>
                  <option value="Resignation because I was given a graceful exit (but I can explain what happened).">Resignation because I was given a graceful exit (but I can explain what happened).</option>
                  <option value="Terminated due to a case but I can explain this to HR.">Terminated due to a case but I can explain this to HR.</option>
                  <option value="Terminated due to AWOL but I can explain this to HR.">Terminated due to AWOL but I can explain this to HR.</option>
                  <option value="Not Applicable">Not Applicable</option>
                  <option value="Others. Inform HR if you choose 'Others'">Others. Inform HR if you choose "Others"</option>
                </select>
              </div>

              <div className="form-group">
                <label>Clearance & COE status *</label>
                <select 
                  className="form-select" 
                  value={formData.clearanceAndCoe9th} 
                  onChange={(e) => handleChoice('clearanceAndCoe9th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.">Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.</option>
                  <option value="No, I did not process an Exit Clearance with this Company.">No, I did not process an Exit Clearance with this Company.</option>
                  <option value="I already processed my Exit Clearance but I have not received by Certificate of Employment.">I already processed my Exit Clearance but I have not received by Certificate of Employment.</option>
                  <option value="For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.">For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.</option>
                  <option value="The Company is already closed.">The Company is already closed.</option>
                  <option value="The Company is located outside the Philippines.">The Company is located outside the Philippines.</option>
                  <option value="Not Applicable.">Not Applicable.</option>
                </select>
              </div>

              </div>

              <div className="employment-block">
              <p className="employment-block-title">Employment History (10th)</p>
              <div className="form-group">
                <label>Company name, role/position, and employment period *</label>
                <p className="input-hint input-hint-muted">
                  For example:<br/>
                  <strong>Delta Prime, Inc.</strong><br/>
                  <strong>Outbound Sales Agent</strong><br/>
                  <strong>January 1, 2024 - March 1, 2025</strong><br/><br/>
                  Write <strong>"NA"</strong> if not applicable.
                </p>
                <textarea 
                  placeholder="Your answer" 
                  value={formData.employmentHistory10th} 
                  onChange={(e) => handleChoice('employmentHistory10th', e.target.value)} 
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Reason for separation *</label>
                <select 
                  className="form-select" 
                  value={formData.reasonForSeparation10th} 
                  onChange={(e) => handleChoice('reasonForSeparation10th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Resignation - Attendance Issues">Resignation - Attendance Issues</option>
                  <option value="Resignation - Benefits Issue">Resignation - Benefits Issue</option>
                  <option value="Resignation - Better Pay">Resignation - Better Pay</option>
                  <option value="Resignation - Career Opportunity">Resignation - Career Opportunity</option>
                  <option value="Resignation - Conflict with a co-worker">Resignation - Conflict with a co-worker</option>
                  <option value="Resignation - Desire to pursue further education">Resignation - Desire to pursue further education</option>
                  <option value="Resignation - Family Reason">Resignation - Family Reason</option>
                  <option value="Resignation - Health Reason">Resignation - Health Reason</option>
                  <option value="Resignation - Incompatibility with company values or mission">Resignation - Incompatibility with company values or mission</option>
                  <option value="Resignation - Lack of job security">Resignation - Lack of job security</option>
                  <option value="Resignation - Lack of recognition or appreciation">Resignation - Lack of recognition or appreciation</option>
                  <option value="Resignation - Legal or compliance issues of the Company">Resignation - Legal or compliance issues of the Company</option>
                  <option value="Resignation - Limited training and development opportunities">Resignation - Limited training and development opportunities</option>
                  <option value="Resignation - Management Issue">Resignation - Management Issue</option>
                  <option value="Resignation - Overwhelming workload or unrealistic expectations">Resignation - Overwhelming workload or unrealistic expectations</option>
                  <option value="Resignation - Personal business ventures or entrepreneurship">Resignation - Personal business ventures or entrepreneurship</option>
                  <option value="Resignation - Poor leadership or communication gaps">Resignation - Poor leadership or communication gaps</option>
                  <option value="Resignation - Relocation or migration (local or abroad)">Resignation - Relocation or migration (local or abroad)</option>
                  <option value="Resignation - Toxic workplace culture">Resignation - Toxic workplace culture</option>
                  <option value="Resignation - Unclear career path or promotion delays">Resignation - Unclear career path or promotion delays</option>
                  <option value="Resignation - Work Burn Out">Resignation - Work Burn Out</option>
                  <option value="Resignation - Work Location Issue">Resignation - Work Location Issue</option>
                  <option value="Resignation - Personal Reason">Resignation - Personal Reason</option>
                  <option value="Resignation - Mental Health Reason">Resignation - Mental Health Reason</option>
                  <option value="Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)">Resignation due to Company instability (e.g., constant layoffs, restructuring, delayed salaries)</option>
                  <option value="Closure of Company Business">Closure of Company Business</option>
                  <option value="End of Project Contract">End of Project Contract</option>
                  <option value="End of Probationary Contract">End of Probationary Contract</option>
                  <option value="Early Retirement Availment">Early Retirement Availment</option>
                  <option value="Separated due to Redundancy">Separated due to Redundancy</option>
                  <option value="Separated due to Retrenchment">Separated due to Retrenchment</option>
                  <option value="Resignation because I was given a graceful exit (but I can explain what happened).">Resignation because I was given a graceful exit (but I can explain what happened).</option>
                  <option value="Terminated due to a case but I can explain this to HR.">Terminated due to a case but I can explain this to HR.</option>
                  <option value="Terminated due to AWOL but I can explain this to HR.">Terminated due to AWOL but I can explain this to HR.</option>
                  <option value="Not Applicable">Not Applicable</option>
                  <option value="Others. Inform HR if you choose 'Others'">Others. Inform HR if you choose "Others"</option>
                </select>
              </div>

              <div className="form-group">
                <label>Clearance & COE status *</label>
                <select 
                  className="form-select" 
                  value={formData.clearanceAndCoe10th} 
                  onChange={(e) => handleChoice('clearanceAndCoe10th', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.">Yes, I have been cleared from accountabilities and I have a Certificate of Employment with me.</option>
                  <option value="No, I did not process an Exit Clearance with this Company.">No, I did not process an Exit Clearance with this Company.</option>
                  <option value="I already processed my Exit Clearance but I have not received by Certificate of Employment.">I already processed my Exit Clearance but I have not received by Certificate of Employment.</option>
                  <option value="For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.">For some reason, the Company will not allow me to process my exit clearance and I will not be able to get a Certificate of Employment.</option>
                  <option value="The Company is already closed.">The Company is already closed.</option>
                  <option value="The Company is located outside the Philippines.">The Company is located outside the Philippines.</option>
                  <option value="Not Applicable.">Not Applicable.</option>
                </select>
              </div>
              </div>

              <div className="form-group">
                <label>
                  {lang === 'zh' ?
                    "我理解，如果我隐瞒或篡改此处要求的任何信息，将被视为不诚实行为，并成为终止我在 HPBO 就业的理由。我同样理解，在完全符合要求之前，我的薪资将被暂扣。 *" :
                    "I understand that it will be construed as an act of dishonesty and a ground for the TERMINATION of my employment if I mis-declared, concealed or altered any information requested from me here. \n\n I also understand that my salaries will be put hold until I complete and comply with the requirements set forth to me. *"
                  }
                </label>
                <select 
                  className="form-select" 
                  value={formData.applicantDeclarationAgreement} 
                  onChange={(e) => handleChoice('applicantDeclarationAgreement', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I understood this will be a ground for the termination of my employment at HPBO and that my salaries will be put on hold.">Yes, I understood this will be a ground for the termination of my employment at HPBO and that my salaries will be put on hold.</option>
                  <option value="No, I wish to discuss this first with the HR Manager before I proceed with my application.">No, I wish to discuss this first with the HR Manager before I proceed with my application.</option>
                  <option value="No, I will now withdraw my application.">No, I will now withdraw my application.</option>
                </select>
              </div>

              <div className="form-group">
                <label>{lang === 'zh' ? "截至当前日期，您是否曾就业并获得过收入？ *" : "Were you employed and had earned an income anytime up to current date? *"}</label>
                <select 
                  className="form-select" 
                  value={formData.pastEmploymentStatus} 
                  onChange={(e) => handleChoice('pastEmploymentStatus', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I was employed up to current date.">Yes, I was employed up to current date.</option>
                  <option value="No, I was not employed up to current date.">No, I was not employed up to current date.</option>
                </select>
              </div>

              <div className="action-btns">
                <button type="button" className="btn-back" onClick={prevStep}>{backBtnText}</button>
                <button type="button" className="btn-next" onClick={nextStep}>{continueBtnText}</button>
              </div>
            </div>
          )}

          {/* STEP 6: Work Set-up & Documents */}
          {step === 6 && (
            <div className="form-section">
              <h3>{lang === 'zh' ? "工作模式与文件" : "Work Set-Up & Documents"}</h3>
              
              <div className="requirement-sidebar">
                <strong>{lang === 'zh' ? '要求验证' : 'Requirement validation'}</strong>
                <p>
                  {lang === 'zh'
                    ? '如果您填写的 SSS、TIN、PhilHealth 或 Pag-IBIG 信息不正确，您的申请可能无法提交。'
                    : 'If SSS, TIN, PhilHealth, or Pag-IBIG details you provide are incorrect, submission may be blocked.'}
                </p>
              </div>

              {formError && (
                <div className="requirement-alert">
                  {formError.split('\n').map((line, idx) => (
                    <div key={idx}>{line}</div>
                  ))}
                </div>
              )}

              <div className="form-group">
                <label><strong>WORK ARRANGEMENT SET-UP</strong> - Report to Office work arrangement. *</label>
                <p className="form-subtext">{lang === 'zh' ? "您将需要定期到办公室报到。您是否乐意接受每日到办（RTO）安排？" : "You will report to office on a regular basis. Are you amenable to work on daily RTO?"}</p>
                <select 
                  className="form-select" 
                  value={formData.workSetup} 
                  onChange={(e) => handleChoice('workSetup', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, this is clear with me.">Yes, this is clear with me.</option>
                  <option value="No, I will no longer continue with my application.">No, I will no longer continue with my application.</option>
                </select>
              </div>

              <div className="form-group">
                <label><strong>SHIFTING SCHEDULE</strong>: Are you amenable to work on a shifting schedule? *</label>
                <p className="form-subtext">This is dependent on the role requirement.</p>
                <select 
                  className="form-select" 
                  value={formData.shiftingSchedule} 
                  onChange={(e) => handleChoice('shiftingSchedule', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, this is okay and clear with me.">Yes, this is okay and clear with me.</option>
                  <option value="No, this is part of my limitation.">No, this is part of my limitation.</option>
                  <option value="No, but if this becomes a business requirement for my role then I will comply.">No, but if this becomes a business requirement for my role then I will comply.</option>
                </select>
              </div>

              <div className="form-group">
                <label><strong>WORK SCHEDULE</strong>: Are you amenable to work on a night schedule? *</label>
                <p className="form-subtext">This is dependent on the role requirement.</p>
                <select 
                  className="form-select" 
                  value={formData.workSchedule} 
                  onChange={(e) => handleChoice('workSchedule', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, this is okay  and clear with me.">Yes, this is okay  and clear with me.</option>
                  <option value="No, this is part of my limitation.">No, this is part of my limitation.</option>
                  <option value="No, but if this becomes a business requirement for my role then I will comply.">No, but if this becomes a business requirement for my role then I will comply.</option>
                  <option value="No, because I have a health issue.">No, because I have a health issue.</option>
                </select>
              </div>

              <div className="form-group">
                <label>{lang === 'zh' ? "上传您最新的个人履历 / 简历 / 作品集 *" : "Upload your updated Biodata / Resume / Curriculum Vitae / Portfolio *"}</label>
                <div className="upload-box">
                  <input type="file" multiple required onChange={(e) => handleFileChange('resumeFiles', e.target.files)} />
                  <p className="upload-hint">Upload up to 10 supported files: PDF, document, or image. Max 1 GB per file.</p>
                </div>
                {fileUploads.resumeFiles.length > 0 && (
                  <div className="uploaded-files-list">
                    {fileUploads.resumeFiles.map((file, index) => (
                      <div key={`resume-${index}`} className="uploaded-file-item">
                        <a href={URL.createObjectURL(file)} target="_blank" rel="noopener noreferrer">{file.name}</a>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>
                  {lang === 'zh'
                    ? "上传您的出生证明扫描件或照片"
                    : "Upload a scan or picture of your Birth Certificate"}{' '}
                  <span className="field-optional">(Optional)</span>
                </label>
                <div className="upload-box">
                  <input type="file" multiple onChange={(e) => handleFileChange('birthCertificateFiles', e.target.files)} />
                  <p className="upload-hint">Upload up to 5 supported files: PDF, document, or image. Max 10 MB per file.</p>
                </div>
                {fileUploads.birthCertificateFiles.length > 0 && (
                  <div className="uploaded-files-list">
                    {fileUploads.birthCertificateFiles.map((file, index) => (
                      <div key={`birth-${index}`} className="uploaded-file-item">
                        <a href={URL.createObjectURL(file)} target="_blank" rel="noopener noreferrer">{file.name}</a>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Upload your TIN ID, BIR Form, or other BIR document here: <span className="field-optional">(Optional)</span></label>
                <div className="upload-box">
                   <input type="file" onChange={(e) => handleFileChange('tinFiles', e.target.files)} />
                  <p className="upload-hint">Upload 1 supported file. Max 100 MB.</p>
                </div>
                {fileUploads.tinFiles.length > 0 && (
                  <div className="uploaded-files-list">
                    {fileUploads.tinFiles.map((file, index) => (
                      <div key={`tin-${index}`} className="uploaded-file-item">
                        <a href={URL.createObjectURL(file)} target="_blank" rel="noopener noreferrer">{file.name}</a>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {renderOcrStatus('tin')}
              </div>

              <div className="form-group">
                <label>Upload your SSS ID, SSS Form, or other SSS document here: <span className="field-optional">(Optional)</span></label>
                <div className="upload-box">
                  <input type="file" onChange={(e) => handleFileChange('sssFiles', e.target.files)} />
                  <p className="upload-hint">Upload 1 supported file. Max 100 MB.</p>
                </div>
                {fileUploads.sssFiles.length > 0 && (
                  <div className="uploaded-files-list">
                    {fileUploads.sssFiles.map((file, index) => (
                      <div key={`sss-${index}`} className="uploaded-file-item">
                        <a href={URL.createObjectURL(file)} target="_blank" rel="noopener noreferrer">{file.name}</a>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {renderOcrStatus('sss')}
              </div>

              <div className="form-group">
                <label>Upload your Phil Health MID print out, Phil Health ID, or other Phil Health document here: <span className="field-optional">(Optional)</span></label>
                <div className="upload-box">
                  <input type="file" onChange={(e) => handleFileChange('philHealthFiles', e.target.files)} />
                  <p className="upload-hint">Upload 1 supported file. Max 100 MB.</p>
                </div>
                {fileUploads.philHealthFiles.length > 0 && (
                  <div className="uploaded-files-list">
                    {fileUploads.philHealthFiles.map((file, index) => (
                      <div key={`phil-${index}`} className="uploaded-file-item">
                        <a href={URL.createObjectURL(file)} target="_blank" rel="noopener noreferrer">{file.name}</a>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {renderOcrStatus('philhealth')}
              </div>

              <div className="form-group">
                <label>Upload your Pag-IBIG ID or other Pag-IBIG document here: <span className="field-optional">(Optional)</span></label>
                <div className="upload-box">
                  <input type="file" onChange={(e) => handleFileChange('pagIbigFiles', e.target.files)} />
                  <p className="upload-hint">Upload 1 supported file: PDF, document, or image. Max 100 MB.</p>
                </div>
                {fileUploads.pagIbigFiles.length > 0 && (
                  <div className="uploaded-files-list">
                    {fileUploads.pagIbigFiles.map((file, index) => (
                      <div key={`pagibig-${index}`} className="uploaded-file-item">
                        <a href={URL.createObjectURL(file)} target="_blank" rel="noopener noreferrer">{file.name}</a>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {renderOcrStatus('pagibig')}
              </div>

              <div className="form-group">
                <label><strong>COE and Clearances</strong> - Upload a picture or pdf copy of all of your Clearance or COE. (Optional)</label>
                <div className="upload-box">
                  <input type="file" multiple onChange={(e) => handleFileChange('coeFiles', e.target.files)} />
                  <p className="upload-hint">Upload up to 10 supported files: PDF, document, or image. Max 1 GB per file.</p>
                </div>
                {fileUploads.coeFiles.length > 0 && (
                  <div className="uploaded-files-list">
                    {fileUploads.coeFiles.map((file, index) => (
                      <div key={`coe-${index}`} className="uploaded-file-item">
                        <a href={URL.createObjectURL(file)} target="_blank" rel="noopener noreferrer">{file.name}</a>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>NBI Clearance (Optional)</label>
                <p className="input-hint input-hint-warning">Reminder: Without NBI Clearance or proof of application for NBI clearance, your salary will be put on-hold.</p>
                <select 
                  className="form-select" 
                  value={formData.nbiStatus} 
                  onChange={(e) => handleChoice('nbiStatus', e.target.value)} 
                >
                  <option value="" disabled>Choose</option>
                  <option value="NBI clearance is valid as of today.">NBI clearance is valid as of today.</option>
                  <option value="Expired NBI Clearance. I understand that my salary will be put on hold if I failed to submit my NBI Clearance or proof of application for NBI Clearance.">Expired NBI Clearance. I understand that my salary will be put on hold if I failed to submit my NBI Clearance or proof of application for NBI Clearance.</option>
                  <option value="On-going NBI Clearance application as of today. I understand that my salary will be put on hold if I failed to submit my NBI Clearance or proof of application for NBI Clearance.">On-going NBI Clearance application as of today. I understand that my salary will be put on hold if I failed to submit my NBI Clearance or proof of application for NBI Clearance.</option>
                  <option value="No NBI Clearance. I understand that my salary will be put on hold if I failed to submit my NBI Clearance or proof of application for NBI Clearance.">No NBI Clearance. I understand that my salary will be put on hold if I failed to submit my NBI Clearance or proof of application for NBI Clearance.</option>
                </select>
              </div>

              <div className="form-group">
                <label>Upload the scan or picture of your NBI Clearance or proof of NBI clearance application. (Optional)</label>
                <p className="input-hint input-hint-warning">Reminder: Without NBI Clearance or proof of application for NBI Clearance your salary will be put on-hold.</p>
                <div className="upload-box">
                  <input type="file" onChange={(e) => handleFileChange('nbiFiles', e.target.files)} />
                  <p className="upload-hint">Upload 1 supported file: PDF, document, or image. Max 100 MB.</p>
                </div>
                {fileUploads.nbiFiles.length > 0 && (
                  <div className="uploaded-files-list">
                    {fileUploads.nbiFiles.map((file, index) => (
                      <div key={`nbi-${index}`} className="uploaded-file-item">
                        <a href={URL.createObjectURL(file)} target="_blank" rel="noopener noreferrer">{file.name}</a>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label><strong>CURRENT HEALTH STATUS</strong>: Are you <strong>physically</strong> and <strong>mentally</strong> fit to work or is there any medical or psychological condition you need to inform us? *</label>
                <p className="input-hint input-hint-muted">
                  Any applicant who is not physically or mentally fit to work and employed deception related to the declaration of his/her current health status will be dealt with accordingly. Helport will not employ physically or mentally unfit to work.
                  <br/><br/>
                  Helport Philippines Branch Office upholds the principles of Non-discrimination and Equal Opportunity Employment to all applicants regardless of age, gender, educational background, race, skin color, religious affiliation and ethnicity. It subscribes to a reasonable accommodations of people with special needs or benefits which the applicable Philippine laws dearly protect. This should not be confused with a Person With Disability (PWD) who is certified by a competent physician as fit to perform the job role.
                </p>
                <select 
                  className="form-select" 
                  value={formData.currentHealthStatus} 
                  onChange={(e) => handleChoice('currentHealthStatus', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I am physically and mentally fit to work.">Yes, I am physically and mentally fit to work.</option>
                  <option value="No, I have medical condition that will limit me to perform my job role.">No, I have medical condition that will limit me to perform my job role.</option>
                  <option value="I have medical condition but I have a fit to work certificate from my medical doctor.">I have medical condition but I have a fit to work certificate from my medical doctor.</option>
                  <option value="I have sensitive pregnancy that will cause me to be absent sometimes at work.">I have sensitive pregnancy that will cause me to be absent sometimes at work.</option>
                  <option value="I am a person with disability but this will not cause difficulty for me to perform my job.">I am a person with disability but this will not cause difficulty for me to perform my job.</option>
                </select>
              </div>

              <div className="form-group form-divider">
                <label>
                  {lang === 'zh' ?
                    "通过提交您的个人信息，您同意 Helport 菲律宾分公司将您的个人详细信息用于您的工作申请和背景调查。您的信息将在数据库中保留接下来的 12 个月。如果您希望要求 HPBO 在 12 个月之前删除您的信息，请将您的请求发送至下方的电子邮箱地址。 \n\n 电子邮箱地址: hpai_recruitment@helport.ai *" :
                    "By submitting your personal information, you consent Helport Philippines Branch Office to use your personal details related to your Job Application and for the sole purposes of your job application and background check. Your information will be retained on the database for the next 12 months. If you wish to request HPBO to delete your information earlier than 12 months, please send your request to the e-mail address below. \n\n Email Address: hpai_recruitment@helport.ai *"
                  }
                </label>
                <select 
                  className="form-select" 
                  value={formData.dataPrivacyConsent} 
                  onChange={(e) => handleChoice('dataPrivacyConsent', e.target.value)} 
                  required
                >
                  <option value="" disabled>Choose</option>
                  <option value="Yes, I consent.">Yes, I consent.</option>
                  <option value="No, please delete my details.">No, please delete my details.</option>
                </select>
              </div>
 
              <div className="action-btns">
                <button type="button" className="btn-back" onClick={prevStep}>{backBtnText}</button>
                <button type="submit" className="btn-submit">
                  {lang === 'zh' ? "提交申请" : "Submit Application"}
                </button>
              </div>
            </div>
          )}   
          
        </form>
      </div> 
    </div>
  );
};

export default ApplyForm;